import {
  updateProject, deleteProject, deleteSubtask, updateSubtask, updateCallout,
  fetchAllWorkflowTemplates, fetchLatestWorkflowTemplate,
  createWorkflowTemplate, updateWorkflowTemplate, deleteWorkflowTemplate,
  deleteProjectSubtasks, createSubtasks,
  deleteCallout, createTechnician,
} from '../lib/api'
import { logChange } from '../lib/auditLog'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef, Fragment } from 'react'
import { DEPT_COLORS, darkenHex } from '../lib/deptColors'
import ExportButton from './ExportButton'
import DayPickerPopover from './DayPickerPopover'
import SubtaskModal from './SubtaskModal'
import ProjectModal from './ProjectModal'
import CalloutModal from './CalloutModal'
import ProjectChartsPanel from './ProjectChartsPanel'
import CustomerUpdatePanel from './CustomerUpdatePanel'
import TraxxallDueList, { prefetchTraxxallData, buildActualsMap } from './TraxxallDueList'
import FleetDueCalendar from './FleetDueCalendar'
import { buildAircraftProjections, buildToleranceExpiryProjections } from '../lib/utilizationProjection'
import { legsTooltip } from '../lib/flightUtils'
import { isDepartureOverdue, isArrivalOverdue } from '../lib/constants'
import { getUSHolidays, isOffDay, addBusinessDays, businessDaysBetween, businessDayDelta, nextBusinessDay } from '../lib/businessDays'
import { generateQASubtasks, QA_MILESTONE_NAMES, getQASeededSet, markQASeeded } from '../lib/qaMilestones'

function hexToHsl(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255
  const g = parseInt(hex.slice(3, 5), 16) / 255
  const b = parseInt(hex.slice(5, 7), 16) / 255
  const max = Math.max(r, g, b), min = Math.min(r, g, b)
  const l = (max + min) / 2
  if (max === min) return [0, 0, l]
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h
  switch (max) {
    case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break
    case g: h = ((b - r) / d + 2) / 6; break
    default: h = ((r - g) / d + 4) / 6
  }
  return [h, s, l]
}

function hslToRgb(h, s, l) {
  if (s === 0) { const v = Math.round(l * 255); return `rgb(${v},${v},${v})` }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const hue = (t) => {
    if (t < 0) t += 1; if (t > 1) t -= 1
    if (t < 1/6) return p + (q - p) * 6 * t
    if (t < 1/2) return q
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6
    return p
  }
  return `rgb(${Math.round(hue(h + 1/3) * 255)},${Math.round(hue(h) * 255)},${Math.round(hue(h - 1/3) * 255)})`
}

function subtaskBarStyle(hex, status, dept) {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  if (status === 'Scheduled') {
    const fill = `rgb(${Math.round(r + (255 - r) * 0.7)},${Math.round(g + (255 - g) * 0.7)},${Math.round(b + (255 - b) * 0.7)})`
    return { backgroundColor: fill, borderLeft: `3px solid ${hex}`, textColor: '#111827' }
  }
  if (status === 'Complete') {
    const [h, s, l] = hexToHsl(hex)
    return { backgroundColor: hslToRgb(h, s * 0.25, l), textColor: 'white' }
  }
  return { backgroundColor: hex, textColor: 'white' }
}

const DEPT_LABELS = {
  MX: 'Maintenance', AVI: 'Avionics', INT: 'Interior',
  PA: 'Paint', QA: 'Inspection', EXT: 'External',
}

function subtaskDepts(s) {
  return s.departments && s.departments.length > 0 ? s.departments : [s.department]
}

// ── UTILITIES ─────────────────────────────────────────────────────────────────

function addDays(date, n) {
  const d = new Date(date)
  d.setDate(d.getDate() + n)
  return d
}

function toISO(date) { return date.toISOString().slice(0, 10) }

function fmtShort(dateStr) {
  if (!dateStr) return '—'
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function fmtDow(date) { return date.toLocaleDateString('en-US', { weekday: 'short' }) }
function fmtMd(date) { return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) }

function snapToWeekStart(date, weekStartDay = 1) {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  const daysBack = (d.getDay() - weekStartDay + 7) % 7
  d.setDate(d.getDate() - daysBack)
  return d
}

function buildChunks(startDate, endDate, chunkSize = 14) {
  const chunks = []
  let cs = new Date(startDate); cs.setHours(0, 0, 0, 0)
  const end = new Date(endDate); end.setHours(0, 0, 0, 0)
  while (cs <= end) {
    chunks.push(Array.from({ length: chunkSize }, (_, i) => {
      const d = new Date(cs)
      d.setDate(d.getDate() + i)
      return d
    }))
    cs.setDate(cs.getDate() + chunkSize)
  }
  return chunks
}

function assignBars(subtasks, chunkDays) {
  const chunkISOs = chunkDays.map(d => toISO(d))
  const bars = []
  subtasks.forEach(subtask => {
    const startIdx = chunkISOs.findIndex(iso => iso >= subtask.start_date)
    const endIdx = chunkISOs.reduce((last, iso, i) => iso <= subtask.end_date ? i : last, -1)
    if (startIdx === -1 || endIdx === -1 || startIdx > endIdx) return
    bars.push({ subtask, startCol: startIdx, endCol: endIdx, span: endIdx - startIdx + 1 })
  })
  bars.sort((a, b) =>
    (a.subtask.sort_order ?? 9999) - (b.subtask.sort_order ?? 9999) ||
    a.startCol - b.startCol
  )
  const rows = []
  bars.forEach(bar => {
    let row = 0
    while (rows.some(r => r.row === row && r.startCol <= bar.endCol && r.endCol >= bar.startCol)) row++
    rows.push({ ...bar, row })
  })
  return rows
}

// ── PROJECT HEADER ────────────────────────────────────────────────────────────

function ProjectHeader({
  project, onEdit, onDelete, onHistory, onNotes, notesCount = 0, onUpdateAsOf, onUpdateDates,
  projectTechs = [], projectTechSet = null, allTechnicians = [],
  subtasks = [], subtaskTechMap = {},
  projectTechDates = [], onAddTechDate, onRemoveTechDate,
  onAddProjectTech, onRemoveProjectTech, onWorkOrderClick,
}) {
  const { isTechnician, profile } = useAuth()
  const orgDepts = profile?.orgs?.settings?.departments ?? []
  const orgDeptMap = Object.fromEntries(orgDepts.map(d => [d.key, d]))
  const inspectionDeptKeys = new Set(orgDepts.filter(d => d.isInspection).map(d => d.key))
  const [editingDate, setEditingDate] = useState(null) // 'arrival' | 'departure' | null
  const [dateInput, setDateInput] = useState('')
  const [showTechPicker, setShowTechPicker] = useState(false)
  const [pickerDropdownPos, setPickerDropdownPos] = useState(null)
  const [addingNewTech, setAddingNewTech] = useState(null) // { dept, first_name, last_name }
  const [savingNewTech, setSavingNewTech] = useState(false)
  const pickerRef = useRef(null)

  // Date-range popover state
  const [datePopoverTechId, setDatePopoverTechId] = useState(null)
  const [newRangeStart, setNewRangeStart] = useState('')
  const [newRangeEnd, setNewRangeEnd] = useState('')
  const [savingRange, setSavingRange] = useState(false)
  const [popoverAlignRight, setPopoverAlignRight] = useState(false)
  const datePopoverRef = useRef(null)

  // Close picker on outside click
  useEffect(() => {
    if (!showTechPicker) return
    const handler = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setShowTechPicker(false)
        setAddingNewTech(null)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showTechPicker])

  // Flip popover left/right so it never overflows the viewport
  useLayoutEffect(() => {
    if (!datePopoverTechId || !datePopoverRef.current) return
    const rect = datePopoverRef.current.getBoundingClientRect()
    setPopoverAlignRight(rect.right > window.innerWidth - 8)
  }, [datePopoverTechId])

  // Close date popover on outside click
  useEffect(() => {
    if (!datePopoverTechId) return
    const handler = (e) => {
      if (datePopoverRef.current && !datePopoverRef.current.contains(e.target)) {
        setDatePopoverTechId(null)
        setNewRangeStart('')
        setNewRangeEnd('')
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [datePopoverTechId])

  const saveNewTech = async () => {
    if (!addingNewTech?.first_name?.trim() || !addingNewTech?.last_name?.trim()) return
    setSavingNewTech(true)
    try {
      const { data: newTech, error } = await createTechnician({
        first_name: addingNewTech.first_name.trim(),
        last_name: addingNewTech.last_name.trim(),
        department: addingNewTech.dept,
      })
      if (error) throw error
      if (onAddProjectTech) await onAddProjectTech(project.id, newTech.id)
      setAddingNewTech(null)
      setShowTechPicker(false)
    } catch (err) {
      console.error('Error creating technician:', err)
    } finally {
      setSavingNewTech(false)
    }
  }

  const saveRange = async () => {
    if (!newRangeStart && !newRangeEnd) return
    setSavingRange(true)
    try {
      await onAddTechDate(project.id, datePopoverTechId, newRangeStart || null, newRangeEnd || null)
      setNewRangeStart('')
      setNewRangeEnd('')
    } finally {
      setSavingRange(false)
    }
  }

  const todayISO = useMemo(() => new Date().toISOString().slice(0, 10), [])

  // Build per-tech date range lookup for this project
  const techDateRangesMap = useMemo(() => {
    const map = {}
    projectTechDates.forEach(d => {
      if (!map[d.technician_id]) map[d.technician_id] = []
      map[d.technician_id].push(d)
    })
    return map
  }, [projectTechDates])

  // Determine which techs are visible in the header based on assignment level and subtask/date ranges
  const visibleTechs = useMemo(() => {
    if (!project) return []
    const projectSubtasks = subtasks.filter(s => s.project_id === project.id)
    return projectTechs.filter(tech => {
      // Inspectors are always visible for the full duration of the project
      if (inspectionDeptKeys.has(tech.department)) return true
      if (projectTechSet?.has(tech.id)) {
        // Explicit date ranges take priority — show for any range that is current or upcoming (not fully expired)
        const ranges = techDateRangesMap[tech.id] || []
        if (ranges.length > 0) {
          return ranges.some(r => !r.end_date || r.end_date >= todayISO)
        }
        // Check active subtasks before any date gate (handles pre-arrival subtasks like QA Incoming)
        const activeDeptToday = projectSubtasks.filter(s =>
          subtaskDepts(s).includes(tech.department) &&
          (s.start_date || '') <= todayISO && (s.end_date || '9999-12-31') >= todayISO
        )
        const activeCrossDeptAssigned = projectSubtasks.some(s =>
          !subtaskDepts(s).includes(tech.department) &&
          (s.start_date || '') <= todayISO && (s.end_date || '9999-12-31') >= todayISO &&
          (subtaskTechMap[s.id] || []).some(t => t.id === tech.id)
        )
        if (activeCrossDeptAssigned) return true
        if (activeDeptToday.some(s => (subtaskTechMap[s.id] || []).some(t => t.id === tech.id))) return true
        if (activeDeptToday.some(s => (subtaskTechMap[s.id] || []).length === 0)) return true
        if (activeDeptToday.length > 0) return false

        // No active subtasks today — fall back to dept/project logic
        const deptSubs = projectSubtasks.filter(s => subtaskDepts(s).includes(tech.department))
        if (deptSubs.length === 0) return true
        const otherDeptActiveToday = projectSubtasks.some(s =>
          !subtaskDepts(s).includes(tech.department) &&
          !subtaskDepts(s).every(d => d === 'QA') &&  // QA runs concurrently — never blocks other depts
          (s.start_date || '') <= todayISO && (s.end_date || '9999-12-31') >= todayISO
        )
        return !otherDeptActiveToday
      } else {
        // Subtask-level: show only during the date range of their assigned subtask(s)
        const assignedSubs = projectSubtasks.filter(s =>
          (subtaskTechMap[s.id] || []).some(t => t.id === tech.id)
        )
        return assignedSubs.some(s =>
          (s.start_date || '') <= todayISO && (s.end_date || '9999-12-31') >= todayISO
        )
      }
    })
  }, [project, projectTechs, projectTechSet, subtasks, subtaskTechMap, techDateRangesMap, todayISO, inspectionDeptKeys])

  if (!project) return null
  const aircraft = project.aircraft
  const reg = aircraft?.registration || '—'
  const model = aircraft?.model || null

  const startDateEdit = (field) => {
    const current = field === 'arrival' ? project.arrival_date : project.departure_date
    setDateInput(current || '')
    setEditingDate(field)
  }

  const saveDateEdit = () => {
    if (onUpdateDates) onUpdateDates(editingDate === 'arrival' ? 'arrival_date' : 'departure_date', dateInput || null)
    setEditingDate(null)
  }

  const projectLevelTechIds = projectTechSet instanceof Set ? projectTechSet : new Set()

  return (
    <div className="bg-white border-b border-gray-200 shadow-sm">
      <div className="px-5 pt-3 pb-2 flex flex-col md:flex-row md:items-start md:gap-6 gap-4">

        {/* Department legend */}
        <div className="flex flex-col gap-1 justify-center flex-shrink-0">
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Departments</span>
          <div className="grid grid-cols-2 gap-x-2 gap-y-1">
            {orgDepts.map(d => (
              <div
                key={d.key}
                className="px-2 py-0.5 rounded text-xs font-medium text-white text-center"
                style={{ backgroundColor: d.hex }}
              >
                {d.label ?? d.key}
              </div>
            ))}
          </div>
        </div>

        <div className="w-px self-stretch bg-gray-200 flex-shrink-0" />

        {/* Main project info */}
        <div className="flex-1 min-w-0 flex flex-col items-center">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold text-gray-900 tracking-wide font-body flex items-center gap-2">
              {reg}
              {project.secondary_registration && (
                <span className="ml-2 text-lg font-normal text-gray-400">/ {project.secondary_registration}</span>
              )}
              {project.firm_schedule && (
                <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" title="This project has a firm schedule">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
              )}
            </h2>
            <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full flex-shrink-0 font-accent ${
              project.status === 'Complete' ? 'bg-green-100 text-green-700' :
              project.status === 'In Work'  ? 'bg-blue-100 text-blue-700' :
              project.status === 'TBD'      ? 'bg-yellow-100 text-yellow-800' :
              'bg-gray-100 text-gray-600'
            }`}>{project.status}</span>
            {onEdit && (
              <button onClick={onEdit}
                className="no-print p-1.5 text-gray-400 hover:text-brand-red hover:bg-brand-red/10 rounded-md transition-colors"
                title="Edit project">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
            )}
            {onNotes && (
              <div className="relative no-print">
                <button onClick={onNotes}
                  className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-md transition-colors"
                  title="Project notes">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </button>
                {notesCount > 0 && (
                  <span className="pointer-events-none absolute -top-1 -right-1 min-w-[1rem] h-4 px-0.5 bg-brand-red text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                    {notesCount > 99 ? '99+' : notesCount}
                  </span>
                )}
              </div>
            )}
            {onDelete && (
              <button onClick={onDelete}
                className="no-print p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                title="Delete project">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
            {onHistory && !isTechnician && (
              <button onClick={() => onHistory(project.id, reg)}
                className="no-print p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-md transition-colors"
                title="View edit history">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            )}
          </div>
          {project.workscope && (
            <div className="w-full mt-2 mb-1 bg-amber-50 border-l-4 border-amber-400 rounded-r-md px-3 py-2">
              <div className="text-[10px] font-bold text-amber-600 uppercase tracking-widest mb-0.5">Work Scope</div>
              <p className="text-sm font-semibold text-gray-900 leading-snug">{project.workscope}</p>
            </div>
          )}
          <div className="flex items-center gap-6 flex-wrap border-t border-gray-100 pt-2">
            <div>
              <div className="text-xs text-gray-400 font-accent">Start</div>
              {editingDate === 'arrival' ? (
                <div className="flex items-center gap-1">
                  <input
                    type="date" value={dateInput} onChange={e => setDateInput(e.target.value)}
                    className="text-xs border border-brand-red/30 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-brand-red/20"
                    autoFocus
                    onKeyDown={e => { if (e.key === 'Enter') saveDateEdit(); if (e.key === 'Escape') setEditingDate(null) }}
                  />
                  <button onClick={saveDateEdit} className="text-xs text-brand-red hover:text-brand-red-dark font-medium">Save</button>
                  <button onClick={() => setEditingDate(null)} className="text-xs text-gray-400 hover:text-gray-600">✕</button>
                </div>
              ) : (
                <div
                  className={`text-sm font-semibold font-accent ${isArrivalOverdue(project) ? 'text-red-600' : 'text-gray-700'} ${onUpdateDates ? 'cursor-pointer hover:text-brand-red' : ''}`}
                  onClick={onUpdateDates ? () => startDateEdit('arrival') : undefined}
                  title={onUpdateDates ? 'Click to edit' : undefined}
                >
                  {fmtShort(project.arrival_date)}
                </div>
              )}
            </div>
            <div>
              <div className="text-xs text-gray-400 font-accent">Est. Complete</div>
              {editingDate === 'departure' ? (
                <div className="flex items-center gap-1">
                  <input
                    type="date" value={dateInput} onChange={e => setDateInput(e.target.value)}
                    className="text-xs border border-brand-red/30 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-brand-red/20"
                    autoFocus
                    onKeyDown={e => { if (e.key === 'Enter') saveDateEdit(); if (e.key === 'Escape') setEditingDate(null) }}
                  />
                  <button onClick={saveDateEdit} className="text-xs text-brand-red hover:text-brand-red-dark font-medium">Save</button>
                  <button onClick={() => setEditingDate(null)} className="text-xs text-gray-400 hover:text-gray-600">✕</button>
                </div>
              ) : (
                <div
                  className={`text-sm font-semibold font-accent ${isDepartureOverdue(project) ? 'text-red-600' : 'text-gray-700'} ${onUpdateDates ? 'cursor-pointer hover:text-brand-red' : ''}`}
                  onClick={onUpdateDates ? () => startDateEdit('departure') : undefined}
                  title={onUpdateDates ? 'Click to edit' : undefined}
                >
                  {fmtShort(project.departure_date)}
                </div>
              )}
            </div>
            {project.work_order && (
              <div>
                <div className="text-xs text-gray-400">Work Order{((project.work_orders_extra || []).filter(Boolean).length) > 0 ? 's' : ''}</div>
                <div className="flex flex-wrap gap-1.5 mt-0.5">
                  {[project.work_order, ...(project.work_orders_extra || [])].filter(Boolean).map(wo => (
                    onWorkOrderClick
                      ? <button key={wo} onClick={() => onWorkOrderClick(wo, project.id)}
                          className="text-sm font-semibold text-brand-red hover:text-brand-red-dark hover:underline">
                          {wo}
                        </button>
                      : <span key={wo} className="text-sm font-semibold text-gray-700">{wo}</span>
                  ))}
                </div>
              </div>
            )}
            {project.customer_name && (
              <div>
                <div className="text-xs text-gray-400">Customer</div>
                <div className="text-sm font-semibold text-gray-700">{project.customer_name}</div>
              </div>
            )}
            {model && (
              <div>
                <div className="text-xs text-gray-400">Model</div>
                <div className="text-sm font-semibold text-gray-700">{model}</div>
              </div>
            )}
            {aircraft?.serial_number && (
              <div>
                <div className="text-xs text-gray-400">Serial #</div>
                <div className="text-sm font-semibold text-gray-700">{aircraft.serial_number}</div>
              </div>
            )}
          </div>
        </div>

        {/* ── Technicians block ── */}
        <div className="w-px self-stretch bg-gray-200 flex-shrink-0" />
        <div className="flex-shrink-0 w-full md:w-72 relative" ref={pickerRef}>
          {/* Header row */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Personnel</span>
            {onAddProjectTech && (
              <button
                onClick={() => {
                  if (pickerRef.current) {
                    const rect = pickerRef.current.getBoundingClientRect()
                    setPickerDropdownPos({ top: rect.bottom + 4, left: rect.left })
                  }
                  setShowTechPicker(v => !v)
                }}
                className="no-print text-xs text-brand-red hover:text-brand-red-dark font-medium px-1.5 py-0.5 rounded hover:bg-brand-red/10 transition-colors"
              >
                + Add
              </button>
            )}
          </div>

          {/* Tech pills — grouped and colored by department, split into Technicians / Inspector */}
          {projectTechs.length === 0 ? (
            <p className="text-xs text-gray-400 italic">None assigned</p>
          ) : visibleTechs.length === 0 ? null : (
            <div className="flex flex-col gap-1.5">
              {(() => {
                const byDept = visibleTechs.reduce((acc, tech) => {
                  const d = tech.department || 'Other'
                  ;(acc[d] = acc[d] || []).push(tech)
                  return acc
                }, {})
                const order = Object.keys(DEPT_COLORS)
                const sortedDepts = Object.keys(byDept).sort((a, b) => {
                  const ai = order.indexOf(a), bi = order.indexOf(b)
                  return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi)
                })
                const workerDepts    = sortedDepts.filter(d => !inspectionDeptKeys.has(d))
                const inspectorDepts = sortedDepts.filter(d => inspectionDeptKeys.has(d))
                const renderDeptPills = (dept) => {
                  const hex = orgDeptMap[dept]?.hex || '#9ca3af'
                  return (
                    <div key={dept}>
                      <div className="text-[10px] font-semibold uppercase tracking-wide mb-0.5" style={{ color: darkenHex(hex) }}>{dept}</div>
                      <div className="flex flex-wrap gap-1">
                        {byDept[dept].map(tech => {
                        const isProjectLevel = projectTechSet?.has(tech.id)
                        const hasRanges      = (techDateRangesMap[tech.id] || []).length > 0
                        const isPopoverOpen  = datePopoverTechId === tech.id
                        const canSetDates    = isProjectLevel && onAddTechDate
                        return (
                          <div key={tech.id} className="relative">
                            <span
                              className={`inline-flex items-center gap-0.5 text-xs px-2 py-0.5 rounded-full ${canSetDates ? 'cursor-pointer hover:opacity-80' : ''}`}
                              style={{ backgroundColor: hex + '33', color: darkenHex(hex), border: `1px solid ${hex}55` }}
                              onClick={canSetDates ? (e) => { e.stopPropagation(); setDatePopoverTechId(isPopoverOpen ? null : tech.id); setNewRangeStart(''); setNewRangeEnd('') } : undefined}
                              title={canSetDates ? 'Click to set schedule dates' : undefined}
                            >
                              {tech.first_name} {tech.last_name?.[0]}.
                              {hasRanges && (
                                <span className="ml-0.5 w-1.5 h-1.5 rounded-full bg-brand-red flex-shrink-0" title="Has schedule dates" />
                              )}
                              {onRemoveProjectTech && isProjectLevel && !canSetDates && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); onRemoveProjectTech(project.id, tech.id) }}
                                  className="no-print ml-0.5 hover:opacity-60 transition-opacity leading-none"
                                  title="Remove from project"
                                >×</button>
                              )}
                            </span>

                            {/* Date-range popover */}
                            {isPopoverOpen && (
                              <div
                                ref={datePopoverRef}
                                className={`no-print absolute top-full mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-lg w-64 p-3 ${popoverAlignRight ? 'right-0' : 'left-0'}`}
                                onClick={e => e.stopPropagation()}
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-xs font-semibold text-gray-700">{tech.first_name} {tech.last_name?.[0]}. — Schedule</span>
                                  <button
                                    onClick={() => onRemoveProjectTech && onRemoveProjectTech(project.id, tech.id)}
                                    className="text-[10px] text-red-400 hover:text-red-600 font-medium"
                                    title="Remove from project"
                                  >Remove</button>
                                </div>

                                {/* Existing ranges */}
                                {(techDateRangesMap[tech.id] || []).length === 0 ? (
                                  <p className="text-xs text-gray-400 italic mb-2">Set manual date ranges</p>
                                ) : (
                                  <div className="space-y-1 mb-2">
                                    {(techDateRangesMap[tech.id] || []).map(r => (
                                      <div key={r.id} className="flex items-center justify-between text-xs bg-gray-50 rounded-lg px-2 py-1">
                                        <span className="text-gray-700">
                                          {r.start_date || '—'} → {r.end_date || '—'}
                                        </span>
                                        <button
                                          onClick={() => onRemoveTechDate && onRemoveTechDate(r.id)}
                                          className="text-red-400 hover:text-red-600 ml-1 font-medium"
                                        >×</button>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* Add new range */}
                                <div className="border-t border-gray-100 pt-2">
                                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Add range</p>
                                  <div className="flex gap-1.5 mb-1.5">
                                    <input type="date" value={newRangeStart} onChange={e => setNewRangeStart(e.target.value)}
                                      className="flex-1 text-xs border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:border-brand-red"
                                      placeholder="Start" />
                                    <input type="date" value={newRangeEnd} onChange={e => setNewRangeEnd(e.target.value)}
                                      className="flex-1 text-xs border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:border-brand-red"
                                      placeholder="End" />
                                  </div>
                                  <button
                                    onClick={saveRange}
                                    disabled={savingRange || (!newRangeStart && !newRangeEnd)}
                                    className="w-full text-xs font-semibold text-white bg-brand-red hover:bg-brand-red-dark disabled:opacity-50 disabled:cursor-not-allowed px-2 py-1 rounded-lg transition-colors"
                                  >{savingRange ? 'Saving…' : 'Add'}</button>
                                </div>
                              </div>
                            )}
                          </div>
                        )
                      })}
                      </div>
                    </div>
                  )
                }
                return (
                  <>
                    {workerDepts.length > 0 && (
                      <>
                        <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Technicians</div>
                        {workerDepts.map(renderDeptPills)}
                      </>
                    )}
                    {inspectorDepts.length > 0 && (
                      <>
                        <div className="flex items-center gap-1.5 mt-1.5">
                          <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Inspector</span>
                          <div className="flex-1 h-px bg-gray-100" />
                        </div>
                        {inspectorDepts.map(renderDeptPills)}
                      </>
                    )}
                  </>
                )
              })()}
            </div>
          )}

          {/* Tech picker dropdown */}
          {showTechPicker && pickerDropdownPos && (
            <div
              className="no-print fixed z-[200] bg-white border border-gray-200 rounded-xl shadow-lg w-64 max-h-80 overflow-y-auto"
              style={{ top: pickerDropdownPos.top, left: pickerDropdownPos.left }}
            >
              {(() => {
                const renderPickerDept = ({ key: dept, label: deptLabel, hex: deptHex }) => {
                  const deptTechs = allTechnicians.filter(t => t.department === dept)
                  const isAddingHere = addingNewTech?.dept === dept
                  if (deptTechs.length === 0 && !isAddingHere) return null
                  return (
                    <div key={dept}>
                      <div className="flex items-center justify-between px-3 py-1.5 bg-gray-50 border-b border-gray-100 sticky top-0">
                        <div className="flex items-center gap-1.5">
                          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: deptHex }} />
                          <span className="text-xs font-semibold text-gray-500">{deptLabel ?? dept}</span>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); setAddingNewTech({ dept, first_name: '', last_name: '' }) }}
                          className="text-xs text-brand-red hover:text-brand-red-dark font-medium px-1.5 py-0.5 rounded hover:bg-brand-red/10 transition-colors"
                        >
                          + New
                        </button>
                      </div>
                      {deptTechs.map(tech => {
                        const isAssigned = projectLevelTechIds.has(tech.id)
                        return (
                          <button
                            key={tech.id}
                            onClick={() => isAssigned
                              ? onRemoveProjectTech(project.id, tech.id)
                              : onAddProjectTech(project.id, tech.id)
                            }
                            className="w-full flex items-center gap-2.5 px-3 py-1.5 text-sm text-gray-700 hover:bg-brand-red/10 hover:text-brand-red transition-colors border-b border-gray-50 last:border-0 text-left"
                          >
                            <div className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border transition-colors ${
                              isAssigned ? 'bg-brand-red/10 text-brand-red border-brand-red/30' : 'border-gray-300'
                            }`}>
                              {isAssigned && (
                                <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                            {tech.first_name} {tech.last_name}
                          </button>
                        )
                      })}
                      {isAddingHere && (
                        <div className="px-3 py-2.5 bg-brand-red/10 border-b border-brand-red/10">
                          <div className="flex gap-1.5 mb-2">
                            <input
                              autoFocus
                              placeholder="First"
                              value={addingNewTech.first_name}
                              onChange={e => setAddingNewTech(a => ({ ...a, first_name: e.target.value }))}
                              onKeyDown={e => { if (e.key === 'Enter') saveNewTech(); if (e.key === 'Escape') setAddingNewTech(null) }}
                              className="flex-1 min-w-0 text-xs border border-brand-red/30 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-red/20 bg-white"
                            />
                            <input
                              placeholder="Last"
                              value={addingNewTech.last_name}
                              onChange={e => setAddingNewTech(a => ({ ...a, last_name: e.target.value }))}
                              onKeyDown={e => { if (e.key === 'Enter') saveNewTech(); if (e.key === 'Escape') setAddingNewTech(null) }}
                              className="flex-1 min-w-0 text-xs border border-brand-red/30 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-red/20 bg-white"
                            />
                          </div>
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              onClick={() => setAddingNewTech(null)}
                              className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100 transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={saveNewTech}
                              disabled={savingNewTech || !addingNewTech.first_name.trim() || !addingNewTech.last_name.trim()}
                              className="text-xs font-semibold text-white bg-brand-red hover:bg-brand-red-dark disabled:opacity-50 disabled:cursor-not-allowed px-2.5 py-1 rounded transition-colors"
                            >
                              {savingNewTech ? 'Saving…' : 'Save & Add'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                }
                const workerDepts    = orgDepts.filter(d => !inspectionDeptKeys.has(d.key))
                const inspectorDepts = orgDepts.filter(d => inspectionDeptKeys.has(d.key))
                return (
                  <>
                    {workerDepts.map(renderPickerDept)}
                    {inspectorDepts.length > 0 && (
                      <>
                        <div className="px-3 py-1.5 bg-gray-100 border-b border-gray-200 sticky top-0">
                          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Inspector</span>
                        </div>
                        {inspectorDepts.map(renderPickerDept)}
                      </>
                    )}
                  </>
                )
              })()}
            </div>
          )}
        </div>

      </div>
    </div>
  )
}

// ── PROJECT SIDEBAR ───────────────────────────────────────────────────────────

function ProjectSidebar({ projects, selectedId, onSelect, onClose, onWorkOrderClick }) {
  const [search, setSearch] = useState('')
  const [inWorkExpanded, setInWorkExpanded] = useState(true)
  const [scheduledExpanded, setScheduledExpanded] = useState(true)
  const [completeExpanded, setCompleteExpanded] = useState(false)

  const filtered = projects.filter(p => {
    const q = search.toLowerCase()
    const reg      = (p.aircraft?.registration || '').toLowerCase()
    const alt      = (p.secondary_registration || '').toLowerCase()
    const customer = (p.customer_name || '').toLowerCase()
    const wo       = (p.work_order || '').toLowerCase()
    const workscope = (p.workscope || '').toLowerCase()
    return reg.includes(q) || alt.includes(q) || customer.includes(q) ||
      wo.includes(q) || workscope.includes(q)
  })

  const inWork    = filtered.filter(p => p.status === 'In Work')
  const scheduled = filtered.filter(p => p.status === 'Scheduled' || p.status === 'TBD')
  const complete  = filtered.filter(p => p.status === 'Complete')

  const renderProject = (p) => {
    const reg = p.aircraft?.registration || '—'
    const isSelected = p.id === selectedId
    return (
      <button key={p.id} onClick={() => { onSelect(p.id); onClose() }}
        className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
          isSelected ? 'bg-brand-red/10 border-l-4 border-l-brand-red' : ''
        }`}>
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-bold text-gray-900 text-sm flex-shrink-0">{reg}</span>
          {p.workscope && <span className="text-sm text-gray-700 truncate">{p.workscope}</span>}
        </div>
        <div className="flex items-center gap-3 mt-0.5 flex-wrap">
          {p.customer_name && <span className="text-xs text-gray-400">{p.customer_name}</span>}
          {p.work_order && (
            [p.work_order, ...(p.work_orders_extra || [])].filter(Boolean).map(wo => (
              onWorkOrderClick
                ? <button key={wo}
                    onClick={e => { e.stopPropagation(); onWorkOrderClick(wo, p.id) }}
                    className="text-xs text-brand-red hover:text-brand-red-dark hover:underline"
                  >
                    WO: {wo}
                  </button>
                : <span key={wo} className="text-xs text-gray-400">WO: {wo}</span>
            ))
          )}
        </div>
        <div className="text-xs text-gray-400 mt-0.5 font-accent">
          {fmtShort(p.arrival_date)} → {p.departure_date ? fmtShort(p.departure_date) : 'TBD'}
        </div>
      </button>
    )
  }

  const SectionHeader = ({ label, count, color = 'text-gray-600', bg = 'bg-gray-50', collapsible, expanded, onToggle }) => (
    <div
      className={`px-4 py-2 ${bg} border-b border-gray-100 flex items-center justify-between ${collapsible ? 'cursor-pointer hover:bg-gray-100 transition-colors select-none' : ''}`}
      onClick={collapsible ? onToggle : undefined}
    >
      <span className={`text-xs font-semibold uppercase tracking-wide ${color}`}>{label} <span className="font-normal opacity-60">({count})</span></span>
      {collapsible && (
        <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${expanded ? '' : '-rotate-90'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      )}
    </div>
  )

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />
      <div className="fixed left-0 top-0 h-full w-80 bg-white shadow-2xl z-50 flex flex-col">
        <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900">Select Project</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input type="text" placeholder="Search reg, workscope, customer..."
              value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-red/20"
              autoFocus />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {inWork.length === 0 && scheduled.length === 0 && complete.length === 0 && (
            <div className="text-center text-gray-400 text-sm py-8">No projects found</div>
          )}
          {inWork.length > 0 && (
            <div>
              <SectionHeader label="In Work" count={inWork.length} color="text-blue-700" bg="bg-blue-50"
                collapsible expanded={inWorkExpanded} onToggle={() => setInWorkExpanded(v => !v)} />
              {inWorkExpanded && inWork.map(renderProject)}
            </div>
          )}
          {scheduled.length > 0 && (
            <div>
              <SectionHeader label="Scheduled" count={scheduled.length} color="text-gray-600" bg="bg-gray-50"
                collapsible expanded={scheduledExpanded} onToggle={() => setScheduledExpanded(v => !v)} />
              {scheduledExpanded && scheduled.map(renderProject)}
            </div>
          )}
          {complete.length > 0 && (
            <div>
              <SectionHeader label="Complete" count={complete.length} color="text-gray-400" bg="bg-gray-50"
                collapsible expanded={completeExpanded} onToggle={() => setCompleteExpanded(v => !v)} />
              {completeExpanded && complete.map(renderProject)}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

// ── BIWEEKLY GRID ─────────────────────────────────────────────────────────────

function BiweeklyGrid({ chunkDays, bars, pStart, onClickSubtask, showDayCount, onDayClick, projectId, allSubtasks, arrivalDate, departureDate, callouts = [], onAddCallout, onEditCallout, isCollapsed: controlledCollapsed, onToggle, subtaskTechMap = {}, flightsByDate = {}, onReorder, today: propToday }) {
  const { profile } = useAuth()
  const orgDeptMap = useMemo(() => Object.fromEntries((profile?.orgs?.settings?.departments ?? []).map(d => [d.key, d])), [profile?.orgs?.id])
  const [internalCollapsed, setInternalCollapsed] = useState(false)
  const controlled = onToggle !== undefined
  const collapsed = controlled ? controlledCollapsed : internalCollapsed
  const toggle = controlled ? onToggle : () => setInternalCollapsed(c => !c)
  const chunkStart = chunkDays[0]
  const chunkEnd = chunkDays[chunkDays.length - 1]
  const today = propToday || toISO(new Date())
  const numRows = bars.length === 0 ? 1 : Math.max(...bars.map(r => r.row)) + 1
  const isPast = toISO(chunkEnd) < today

  return (
    <div className="border-b border-gray-200 last:border-b-0 workflow-chunk">
      <div
        className="workflow-chunk-header flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors select-none"
        onClick={toggle}
      >
        <div className="flex items-center gap-2">
          <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${collapsed ? '-rotate-90' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
          <span className="text-xs font-semibold text-gray-600">
            {fmtMd(chunkStart)} — {fmtMd(chunkEnd)}
          </span>
          {isPast && <span className="text-xs text-gray-400 italic">past</span>}
        </div>
        <span className="text-xs text-gray-400">{collapsed ? 'Show' : 'Hide'}</span>
      </div>

      {!collapsed && (
        <div className="overflow-x-auto">
          <div style={{ minWidth: chunkDays.length * 50 }}>

            {/* Column headers */}
            <div className="workflow-col-headers grid border-b border-gray-200" style={{ gridTemplateColumns: `repeat(${chunkDays.length}, 1fr)` }}>
              {chunkDays.map((d, i) => {
                const off = isOffDay(d)
                const isToday = toISO(d) === today
                const dayNum = pStart ? Math.round((d - pStart) / 86400000) + 1 : null
                const iso = toISO(d)
                const isArrival = arrivalDate && iso === arrivalDate
                const isDeparture = departureDate && iso === departureDate
                return (
                  <div
                    key={i}
                    className={`relative border-r border-gray-200 last:border-r-0 px-1 py-1.5 text-center cursor-pointer hover:bg-brand-red/5 transition-colors group ${
                      isArrival ? 'bg-green-100' :
                      isDeparture ? 'bg-red-100' :
                      off ? 'bg-gray-100' : 'bg-white'
                    }`}
                    onClick={(e) => {
                      if (!onDayClick) return
                      const subtasksOnDay = (allSubtasks || []).filter(s =>
                        s.start_date <= iso && s.end_date >= iso
                      )
                      onDayClick(iso, subtasksOnDay, projectId, e.currentTarget.getBoundingClientRect())
                    }}
                  >
                    <div className={`text-xs font-medium ${isToday ? 'text-brand-red' : off ? 'text-gray-400' : 'text-gray-500'}`}>
                      {fmtDow(d)}
                    </div>
                    <div className={`text-xs font-bold ${
                      isArrival ? 'text-green-700' :
                      isDeparture ? 'text-red-700' :
                      isToday ? 'text-brand-red' :
                      off ? 'text-gray-400' : 'text-gray-800'
                    }`}>
                      {fmtMd(d)}
                    </div>
                    {showDayCount && dayNum !== null && dayNum > 0 && (
                      <div className={`text-xs ${
                        isArrival ? 'text-green-600' :
                        isDeparture ? 'text-red-500' :
                        off ? 'text-gray-300' : 'text-gray-400'
                      }`}>
                        D{dayNum}
                      </div>
                    )}
                    {onAddCallout && (
                      <button
                        className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-brand-red text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-10 leading-none"
                        onClick={(e) => { e.stopPropagation(); onAddCallout(iso) }}
                        title="Add callout"
                      >
                        +
                      </button>
                    )}
                    {flightsByDate[iso]?.length > 0 && (
                      <div
                        className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: '#101820', zIndex: 6 }}
                        title={legsTooltip(flightsByDate[iso])}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="#ffffff">
                          <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
                        </svg>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Callout strip — spans multi-day callouts as bars */}
            {(() => {
              const chunkISOs = chunkDays.map(toISO)
              const chunkStart = chunkISOs[0]
              const chunkEnd = chunkISOs[chunkDays.length - 1]
              const visible = callouts.filter(c => {
                const end = c.end_date || c.date
                return c.date <= chunkEnd && end >= chunkStart
              })
              if (visible.length === 0) return null
              const rows = []
              visible.forEach(c => {
                const startIdx = Math.max(0, chunkISOs.findIndex(iso => iso >= c.date))
                const endDate = c.end_date || c.date
                const endIdx = chunkISOs.reduce((last, iso, i) => iso <= endDate ? i : last, -1)
                if (startIdx === -1 || endIdx < 0) return
                let row = 0
                while (rows.some(r => r.row === row && r.startIdx <= endIdx && r.endIdx >= startIdx)) row++
                rows.push({ c, startIdx, endIdx, span: endIdx - startIdx + 1, row })
              })
              const maxRow = rows.length === 0 ? 0 : Math.max(...rows.map(r => r.row)) + 1
              const byRow = Array.from({ length: maxRow }, (_, i) => rows.filter(r => r.row === i))
              return (
                <div className="border-b border-gray-100">
                  {byRow.map((rowItems, rowIdx) => (
                    <div key={rowIdx} className="grid py-1" style={{ gridTemplateColumns: `repeat(${chunkDays.length}, 1fr)` }}>
                      {rowItems.map(({ c, startIdx, span }) => {
                        const isStatus = c.type === 'status'
                        return (
                          <div
                            key={c.id}
                            className="cursor-pointer hover:opacity-80 transition-opacity px-1.5 py-0.5 rounded mx-0.5"
                            style={{
                              gridColumn: `${startIdx + 1} / span ${span}`,
                              backgroundColor: isStatus ? '#dcfce7' : '#fee2e2',
                              border: `1px solid ${isStatus ? '#86efac' : '#fca5a5'}`,
                            }}
                            onClick={() => onEditCallout && onEditCallout(c)}
                          >
                            <p className="text-xs font-medium leading-tight break-words text-center font-accent"
                              style={{ color: isStatus ? '#15803d' : '#991b1b' }}>
                              {c.text}
                            </p>
                          </div>
                        )
                      })}
                    </div>
                  ))}
                </div>
              )
            })()}

            {/* Bars area — also used for print/export */}
            <div className="workflow-bars-absolute relative" style={{ height: numRows * 32 + 8 }}>
              {chunkDays.some(d => toISO(d) === today) && (() => {
                const idx = chunkDays.findIndex(d => toISO(d) === today)
                return (
                  <div className="absolute inset-y-0 w-0.5 bg-brand-red z-10 pointer-events-none"
                    style={{ left: `${((idx + 0.5) / chunkDays.length) * 100}%` }} />
                )
              })()}
              <div className="absolute inset-0 grid pointer-events-none" style={{ gridTemplateColumns: `repeat(${chunkDays.length}, 1fr)` }}>
                {chunkDays.map((d, i) => (
                  <div key={i} className={isOffDay(d) ? 'bg-gray-100' : ''} />
                ))}
              </div>
              {bars.map((bar, bi) => {
                const barStyle = subtaskBarStyle(orgDeptMap[bar.subtask.department]?.hex || '#9ca3af', bar.subtask.status, bar.subtask.department)
                const colWidth = 100 / chunkDays.length
                const barTechs = subtaskTechMap[bar.subtask.id] || []
                const startActuallyInChunk = bar.subtask.start_date >= toISO(chunkDays[0])
                const endActuallyInChunk = bar.subtask.end_date <= toISO(chunkDays[chunkDays.length - 1])
                let leadingOff = 0
                if (startActuallyInChunk) {
                  for (let c = bar.startCol; c < bar.startCol + bar.span; c++) {
                    if (isOffDay(chunkDays[c])) leadingOff++
                    else break
                  }
                }
                let trailingOff = 0
                if (endActuallyInChunk) {
                  for (let c = bar.startCol + bar.span - 1; c >= bar.startCol + leadingOff; c--) {
                    if (isOffDay(chunkDays[c])) trailingOff++
                    else break
                  }
                }
                // Shift text past any leading off-day columns so it starts in the visible area.
                let textLeadingOff = 0
                for (let c = bar.startCol; c < bar.startCol + bar.span; c++) {
                  if (isOffDay(chunkDays[c])) textLeadingOff++
                  else break
                }
                const reorderIconColor = barStyle.textColor === 'white' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.45)'
                return (
                  <Fragment key={bi}>
                  <div
                    className="workflow-screen-bar absolute cursor-pointer hover:opacity-80 transition-opacity rounded overflow-hidden group/bar"
                    style={{
                      left: `${bar.startCol * colWidth}%`,
                      width: `${bar.span * colWidth}%`,
                      top: bar.row * 32 + 4,
                      height: 26,
                      backgroundColor: barStyle.backgroundColor,
                      borderLeft: barStyle.borderLeft,
                    }}
                    onClick={() => onClickSubtask(bar.subtask)}
                  >
                    <div className="absolute inset-y-0 flex items-center gap-1.5 px-2 overflow-hidden"
                      style={{ left: textLeadingOff > 0 ? `${(textLeadingOff / bar.span) * 100}%` : 0, right: 0, color: barStyle.textColor }}>
                      {/* Normal view: task name only */}
                      <span className="text-xs truncate bar-std-label">
                        {bar.subtask.task_name}
                      </span>

                      {/* Tech print view: task name + name pills */}
                      <span className="bar-tech-label items-center gap-1 min-w-0 overflow-hidden">
                        <span className="text-xs font-semibold shrink-0 whitespace-nowrap">
                          {bar.subtask.task_name}
                        </span>
                        {barTechs.length > 0 && (
                          <>
                            <span className="text-xs shrink-0 mx-0.5" style={{ opacity: 0.5 }}>·</span>
                            {barTechs.map(t => (
                              <span
                                key={t.id}
                                className="text-xs shrink-0 whitespace-nowrap px-2 rounded-full leading-5"
                                style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}
                              >
                                {t.first_name} {t.last_name?.[0]}.
                              </span>
                            ))}
                          </>
                        )}
                      </span>
                    </div>

                    {onReorder && (
                      <div
                        className="absolute top-0 h-full flex items-center gap-px opacity-0 group-hover/bar:opacity-100 transition-opacity no-print"
                        style={{ right: trailingOff > 0 ? `calc(${(trailingOff / bar.span) * 100}% + 4px)` : '4px' }}
                        onClick={e => e.stopPropagation()}
                      >
                        <button
                          className="w-4 h-4 rounded flex items-center justify-center"
                          style={{ color: reorderIconColor }}
                          onClick={() => onReorder(bar.subtask, 'up', bars.map(b => b.subtask))}
                          title="Move up"
                        >
                          <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
                            <path d="M5 15l7-7 7 7" />
                          </svg>
                        </button>
                        <button
                          className="w-4 h-4 rounded flex items-center justify-center"
                          style={{ color: reorderIconColor }}
                          onClick={() => onReorder(bar.subtask, 'down', bars.map(b => b.subtask))}
                          title="Move down"
                        >
                          <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                  </Fragment>
                )
              })}
              {/* Off-day mask: single layer above all bars covering off-day columns in both screen and print */}
              <div className="absolute inset-0 pointer-events-none" style={{ display: 'grid', gridTemplateColumns: `repeat(${chunkDays.length}, 1fr)`, zIndex: 1 }}>
                {chunkDays.map((d, i) => (
                  <div key={i} style={{ backgroundColor: isOffDay(d) ? '#f3f4f6' : undefined }} />
                ))}
              </div>
              {/* Edge caps: restore bar color above the mask for leading/trailing off-day columns.
                  A bar that starts or ends on an off-day should show its color on those edge columns. */}
              {bars.map((bar, bi) => {
                const barStyle = subtaskBarStyle(orgDeptMap[bar.subtask.department]?.hex || '#9ca3af', bar.subtask.status, bar.subtask.department)
                const colWidth = 100 / chunkDays.length
                const startActuallyInChunk = bar.subtask.start_date >= toISO(chunkDays[0])
                const endActuallyInChunk = bar.subtask.end_date <= toISO(chunkDays[chunkDays.length - 1])
                let leadingOff = 0
                if (startActuallyInChunk) {
                  for (let c = bar.startCol; c < bar.startCol + bar.span; c++) {
                    if (isOffDay(chunkDays[c])) leadingOff++
                    else break
                  }
                }
                let trailingOff = 0
                if (endActuallyInChunk) {
                  for (let c = bar.startCol + bar.span - 1; c >= bar.startCol + leadingOff; c--) {
                    if (isOffDay(chunkDays[c])) trailingOff++
                    else break
                  }
                }
                if (leadingOff === 0 && trailingOff === 0) return null
                const caps = []
                for (let r = 0; r < leadingOff; r++) caps.push(r)
                for (let r = bar.span - trailingOff; r < bar.span; r++) caps.push(r)
                return (
                  <Fragment key={`caps-${bi}`}>
                    {caps.map(ri => {
                      const isFirst = ri === 0
                      const isLast = ri === bar.span - 1
                      return (
                        <div key={ri} className="absolute pointer-events-none"
                          style={{
                            left: `${(bar.startCol + ri) * colWidth}%`,
                            width: `${colWidth}%`,
                            top: bar.row * 32 + 4,
                            height: 26,
                            backgroundColor: barStyle.backgroundColor,
                            borderLeft: isFirst ? barStyle.borderLeft : undefined,
                            borderRadius: isFirst && isLast ? 4 : isFirst ? '4px 0 0 4px' : isLast ? '0 4px 4px 0' : 0,
                            zIndex: 2,
                          }}
                        />
                      )
                    })}
                  </Fragment>
                )
              })}
            </div>

            {/* Print-only bar list: absolute layout mirrors workflow-bars-absolute geometry
                so Chrome's print renderer can't collapse row heights and cause overlaps */}
            <div className="workflow-print-barlist" style={{ position: 'relative', height: numRows * 32 + 8 }}>
              <div className="absolute inset-0 grid pointer-events-none" style={{ gridTemplateColumns: `repeat(${chunkDays.length}, 1fr)` }}>
                {chunkDays.map((d, i) => (
                  <div key={i} style={{ backgroundColor: isOffDay(d) ? '#f3f4f6' : 'transparent' }} />
                ))}
              </div>
              {bars.map((bar, bi) => {
                const barStyle = subtaskBarStyle(orgDeptMap[bar.subtask.department]?.hex || '#9ca3af', bar.subtask.status, bar.subtask.department)
                const colWidth = 100 / chunkDays.length
                const barTechs = subtaskTechMap[bar.subtask.id] || []
                const printStartInChunk = bar.subtask.start_date >= toISO(chunkDays[0])
                const printEndInChunk = bar.subtask.end_date <= toISO(chunkDays[chunkDays.length - 1])
                let printLeadingOff = 0
                if (printStartInChunk) {
                  for (let c = bar.startCol; c < bar.startCol + bar.span; c++) {
                    if (isOffDay(chunkDays[c])) printLeadingOff++
                    else break
                  }
                }
                let printTrailingOff = 0
                if (printEndInChunk) {
                  for (let c = bar.startCol + bar.span - 1; c >= bar.startCol + printLeadingOff; c--) {
                    if (isOffDay(chunkDays[c])) printTrailingOff++
                    else break
                  }
                }
                const grayOverlays = []
                for (let c = bar.startCol; c < bar.startCol + bar.span; c++) {
                  const ri = c - bar.startCol
                  if (isOffDay(chunkDays[c]) && ri >= printLeadingOff && ri < bar.span - printTrailingOff) {
                    grayOverlays.push(ri)
                  }
                }
                const printLeftGray  = grayOverlays.length > 0 && grayOverlays[0] === 0
                const printRightGray = grayOverlays.length > 0 && grayOverlays[grayOverlays.length - 1] === bar.span - 1
                return (
                  <Fragment key={bi}>
                    <div
                      className="workflow-print-bar absolute rounded overflow-hidden"
                      style={{
                        left: `${bar.startCol * colWidth}%`,
                        width: `${bar.span * colWidth}%`,
                        top: bar.row * 32 + 4,
                        height: 26,
                        backgroundColor: barStyle.backgroundColor,
                        borderLeft: printLeftGray ? undefined : barStyle.borderLeft,
                        borderRadius: printLeftGray && printRightGray ? 0 : printLeftGray ? '0 4px 4px 0' : printRightGray ? '4px 0 0 4px' : undefined,
                      }}
                    >
                      <div className="absolute inset-y-0 flex items-center gap-1.5 px-2 overflow-hidden"
                        style={{ left: 0, right: 0, color: barStyle.textColor }}>
                        <span className="text-xs truncate bar-std-label">
                          {bar.subtask.task_name}
                        </span>
                        <span className="bar-tech-label items-center gap-1 min-w-0 overflow-hidden">
                          <span className="text-xs font-semibold shrink-0 whitespace-nowrap">
                            {bar.subtask.task_name}
                          </span>
                          {barTechs.length > 0 && (
                            <>
                              <span className="text-xs shrink-0 mx-0.5" style={{ opacity: 0.5 }}>·</span>
                              {barTechs.map(t => (
                                <span
                                  key={t.id}
                                  className="text-xs shrink-0 whitespace-nowrap px-2 rounded-full leading-5"
                                  style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}
                                >
                                  {t.first_name} {t.last_name?.[0]}.
                                </span>
                              ))}
                            </>
                          )}
                        </span>
                      </div>
                      {grayOverlays.map(ri => (
                        <div key={ri} className="absolute inset-y-0 pointer-events-none"
                          style={{ left: `${(ri / bar.span) * 100}%`, width: `${(1 / bar.span) * 100}%`, backgroundColor: '#f3f4f6' }} />
                      ))}
                    </div>
                  </Fragment>
                )
              })}
            </div>

          </div>
        </div>
      )}
    </div>
  )
}

// ── WORKFLOW PAST PERIODS GROUP ───────────────────────────────────────────────

function WorkflowPastPeriodsGroup({ pastChunks, subtasks, pStart, onClickSubtask, onDayClick, projectId, allSubtasks, arrivalDate, departureDate, callouts, onAddCallout, onEditCallout, subtaskTechMap = {}, flightsByDate = {}, onReorder, today }) {
  const [groupOpen, setGroupOpen] = useState(false)
  const [hasBeenOpened, setHasBeenOpened] = useState(false)
  const [collapsedSet, setCollapsedSet] = useState(new Set())

  const toggleGroup = () => {
    if (!hasBeenOpened) {
      setHasBeenOpened(true)
      setCollapsedSet(new Set(pastChunks.map((_, i) => i)))
    }
    setGroupOpen(p => !p)
  }

  const toggleChunk = (idx) => {
    setCollapsedSet(prev => {
      const next = new Set(prev)
      next.has(idx) ? next.delete(idx) : next.add(idx)
      return next
    })
  }

  const visibleIndices = groupOpen
    ? pastChunks.map((_, i) => i)
    : hasBeenOpened
      ? pastChunks.map((_, i) => i).filter(i => !collapsedSet.has(i))
      : []

  return (
    <div className="border-b border-gray-200">
      <div
        className="flex items-center justify-between px-4 py-2 bg-gray-100 cursor-pointer hover:bg-gray-200 transition-colors select-none"
        onClick={toggleGroup}
      >
        <div className="flex items-center gap-2">
          <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${groupOpen ? '' : '-rotate-90'}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
          <span className="text-xs font-semibold text-gray-500">Past Periods</span>
          <span className="text-xs text-gray-400">
            {toISO(pastChunks[0][0])} — {toISO(pastChunks[pastChunks.length - 1][pastChunks[pastChunks.length - 1].length - 1])}
          </span>
        </div>
        <span className="text-xs text-gray-400">{groupOpen ? 'Hide all' : 'Show all'}</span>
      </div>

      {visibleIndices.map(idx => {
        const chunkDays = pastChunks[idx]
        const bars = assignBars(subtasks, chunkDays)
        return (
          <BiweeklyGrid
            key={idx}
            chunkDays={chunkDays}
            bars={bars}
            pStart={pStart}
            onClickSubtask={onClickSubtask}
            showDayCount={true}
            onDayClick={onDayClick}
            projectId={projectId}
            allSubtasks={allSubtasks}
            arrivalDate={arrivalDate}
            departureDate={departureDate}
            callouts={callouts}
            onAddCallout={onAddCallout}
            onEditCallout={onEditCallout}
            subtaskTechMap={subtaskTechMap}
            flightsByDate={flightsByDate}
            isCollapsed={collapsedSet.has(idx)}
            onToggle={() => toggleChunk(idx)}
            onReorder={onReorder}
            today={today}
          />
        )
      })}
    </div>
  )
}

// ── PROJECT VIEW ──────────────────────────────────────────────────────────────

function ProjectView({ project, subtasks, callouts = [], onClickSubtask, onDayClick, onAddCallout, onEditCallout, onDeleteCallout, subtaskTechMap = {}, flightLegs = [], onReorder, snapshotDate }) {
  if (!project) return null

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640
  const chunkSize = isMobile ? 7 : 14
  const { profile } = useAuth()
  const weekStartDay = profile?.orgs?.settings?.scheduling?.weekStartDay ?? 1

  const regs = new Set([project.aircraft?.registration, project.secondary_registration].filter(Boolean))
  const flightsByDate = {}
  for (const leg of flightLegs) {
    if (!regs.has(leg.aircraft_reg)) continue
    if (!flightsByDate[leg.leg_date]) flightsByDate[leg.leg_date] = []
    flightsByDate[leg.leg_date].push(leg)
  }

  const prevBizDay = project.arrival_date ? addBusinessDays(project.arrival_date, -1) : null
  const pStart = snapToWeekStart(new Date((prevBizDay || project.arrival_date) + 'T00:00:00'), weekStartDay)

  const latestSubtaskEnd = subtasks.reduce((max, s) => s.end_date > max ? s.end_date : max, '')
  const pEndBase = project.departure_date || toISO(addDays(pStart, 13))
  const pEnd = new Date((latestSubtaskEnd > pEndBase ? latestSubtaskEnd : pEndBase) + 'T00:00:00')
  pEnd.setHours(0, 0, 0, 0)

  const chunks = buildChunks(pStart, pEnd, chunkSize)
  const today = snapshotDate || toISO(new Date())
  const pastChunks = chunks.filter(days => toISO(days[days.length - 1]) < today)
  const currentChunks = chunks.filter(days => toISO(days[days.length - 1]) >= today)

  return (
    <div>
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">

        {pastChunks.length > 0 && (
          <WorkflowPastPeriodsGroup
            pastChunks={pastChunks}
            subtasks={subtasks}
            pStart={pStart}
            onClickSubtask={onClickSubtask}
            onDayClick={onDayClick}
            projectId={project.id}
            allSubtasks={subtasks}
            arrivalDate={project.arrival_date}
            departureDate={project.departure_date}
            callouts={callouts}
            onAddCallout={onAddCallout}
            onEditCallout={onEditCallout}
            subtaskTechMap={subtaskTechMap}
            flightsByDate={flightsByDate}
            onReorder={onReorder}
            today={today}
          />
        )}

        {currentChunks.map((chunkDays, ci) => {
          const bars = assignBars(subtasks, chunkDays)
          return (
            <BiweeklyGrid
              key={ci}
              chunkDays={chunkDays}
              bars={bars}
              pStart={pStart}
              onClickSubtask={onClickSubtask}
              showDayCount={true}
              onDayClick={onDayClick}
              projectId={project.id}
              allSubtasks={subtasks}
              arrivalDate={project.arrival_date}
              departureDate={project.departure_date}
              callouts={callouts}
              onAddCallout={onAddCallout}
              onEditCallout={onEditCallout}
              subtaskTechMap={subtaskTechMap}
              flightsByDate={flightsByDate}
              onReorder={onReorder}
              today={today}
            />
          )
        })}


      </div>
    </div>
  )
}

// ── BIWEEKLY DEPT VIEW ────────────────────────────────────────────────────────

function BiweeklyDeptView({ project, subtasks, onClickSubtask, onDayClick, subtaskTechMap = {} }) {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640
  const chunkSize = isMobile ? 7 : 14
  const { profile } = useAuth()
  const orgDepts = profile?.orgs?.settings?.departments ?? []
  const weekStartDay = profile?.orgs?.settings?.scheduling?.weekStartDay ?? 1

  const [activeDepts, setActiveDepts] = useState(() => new Set(orgDepts[0] ? [orgDepts[0].key] : []))

  const toggleDept = (dept) => {
    setActiveDepts(prev => {
      const next = new Set(prev)
      if (next.has(dept)) {
        if (next.size === 1) return next
        next.delete(dept)
      } else {
        next.add(dept)
      }
      return next
    })
  }

  const today = new Date(); today.setHours(0, 0, 0, 0)

  const [chunkStart, setChunkStart] = useState(() => snapToWeekStart(new Date(today), weekStartDay))

  const chunkDays = useMemo(() =>
    Array.from({ length: chunkSize }, (_, i) => addDays(chunkStart, i)),
    [chunkStart, chunkSize]
  )

  const navigate = (dir) => setChunkStart(prev => addDays(prev, dir * 7))

  const goToday = () => setChunkStart(snapToWeekStart(new Date(today), weekStartDay))

  const deptSubtasks = useMemo(() => {
    if (!project) return []
    return subtasks
      .filter(s => s.project_id === project.id && subtaskDepts(s).some(d => activeDepts.has(d)))
      .map(s => ({ ...s, _registration: project.aircraft?.registration }))
  }, [subtasks, project, activeDepts])

  const bars = useMemo(() => assignBars(deptSubtasks, chunkDays), [deptSubtasks, chunkDays])

  if (!project) return null

  const pStartPrevBiz = project.arrival_date ? addBusinessDays(project.arrival_date, -1) : null
  const pStart = snapToWeekStart(new Date((pStartPrevBiz || project.arrival_date) + 'T00:00:00'), weekStartDay)

  return (
    <div id="workflows-dept-export">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="no-print flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50 flex-wrap gap-2">
          <div className="flex items-center gap-1 flex-wrap">
            {orgDepts.map(({ key: dept, label: deptLabel, hex: deptHex }) => {
              const isActive = activeDepts.has(dept)
              return (
                <button key={dept} onClick={() => toggleDept(dept)}
                  style={isActive ? { backgroundColor: deptHex + '20', color: deptHex, borderColor: 'transparent' } : {}}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
                    isActive ? 'border-transparent' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                  }`}>
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: deptHex }} />
                  {deptLabel ?? dept}
                </button>
              )
            })}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => navigate(-1)} className="p-1.5 rounded hover:bg-gray-200 transition-colors text-gray-500">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button onClick={goToday} className="text-xs px-2 py-1 rounded border border-gray-200 hover:bg-gray-100 transition-colors">
              Today
            </button>
            <button onClick={() => navigate(1)} className="p-1.5 rounded hover:bg-gray-200 transition-colors text-gray-500">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
        <BiweeklyGrid
          chunkDays={chunkDays}
          bars={bars}
          pStart={pStart}
          onClickSubtask={onClickSubtask}
          showDayCount={true}
          onDayClick={onDayClick}
          projectId={project.id}
          allSubtasks={deptSubtasks}
          subtaskTechMap={subtaskTechMap}
        />
      </div>
    </div>
  )
}

// ── Update Template Modal ─────────────────────────────────────────────────────

function UpdateTemplateModal({ template, projectSubtasks, onUpdate, onClose }) {
  const [name, setName] = useState(template.name)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [err, setErr] = useState(null)

  const handleUpdate = async () => {
    if (!name.trim() || saving) return
    setSaving(true); setErr(null)
    try {
      await onUpdate(template.id, name.trim())
      setSaved(true)
      setTimeout(onClose, 900)
    } catch {
      setErr('Failed to update — please try again.')
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-[420px] p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold text-gray-800">Update Template</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <p className="text-sm text-gray-500">
          This will replace <span className="font-semibold text-gray-700">"{template.name}"</span> with the current workflow ({projectSubtasks.length} task{projectSubtasks.length !== 1 ? 's' : ''}).
        </p>

        <div>
          <label className="block text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1.5">Template Name</label>
          <input
            autoFocus
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleUpdate()}
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-red"
          />
        </div>

        {err && <p className="text-xs text-red-500">{err}</p>}

        <div className="flex gap-2 justify-end pt-1">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors">Cancel</button>
          <button
            onClick={handleUpdate}
            disabled={!name.trim() || saving || saved}
            className="text-sm px-4 py-2 rounded-lg bg-brand-red text-white font-medium hover:bg-brand-red-dark disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {saved ? '✓ Updated!' : saving ? 'Updating…' : 'Update Template'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Save Template Modal ───────────────────────────────────────────────────────

function SaveTemplateModal({ project, projectSubtasks, onSave, onOverride, onClose }) {
  const { profile } = useAuth()
  const orgDepts = profile?.orgs?.settings?.departments ?? []
  const deptMap = Object.fromEntries(orgDepts.map(d => [d.key, d]))

  const [mode, setMode]           = useState('new') // 'new' | 'override'
  const [name, setName]           = useState(project?.workscope || '')
  const [templates, setTemplates] = useState([])
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const [selectedTpl, setSelectedTpl] = useState(null)
  const [saving, setSaving]       = useState(false)
  const [saved, setSaved]         = useState(false)
  const [err, setErr]             = useState(null)

  const hasTasks = projectSubtasks.length > 0
  const deptGroups = orgDepts.map(d => d.key).filter(d => projectSubtasks.some(s => s.department === d))

  useEffect(() => {
    if (mode !== 'override') return
    setTemplatesLoading(true)
    fetchAllWorkflowTemplates()
      .then(({ data }) => { setTemplates(data || []); setTemplatesLoading(false) })
      .catch(() => setTemplatesLoading(false))
  }, [mode])

  const selectTemplate = (tpl) => {
    setSelectedTpl(tpl)
    setName(tpl.name)
  }

  const handleSave = async () => {
    if (!hasTasks || saving) return
    if (mode === 'new' && !name.trim()) return
    if (mode === 'override' && !selectedTpl) return
    setSaving(true); setErr(null)
    try {
      if (mode === 'override') {
        await onOverride(selectedTpl.id, name.trim() || selectedTpl.name)
      } else {
        await onSave(name.trim())
      }
      setSaved(true)
      setTimeout(onClose, 900)
    } catch {
      setErr('Failed to save — please try again.')
      setSaving(false)
    }
  }

  const canSave = hasTasks && !saving && !saved &&
    (mode === 'new' ? name.trim() : !!selectedTpl)

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-[460px] p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold text-gray-800">Save as Template</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Mode toggle */}
        <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm">
          <button
            type="button"
            onClick={() => { setMode('new'); setSelectedTpl(null); setName(project?.workscope || '') }}
            className={`flex-1 py-1.5 font-medium transition-colors ${mode === 'new' ? 'bg-brand-red text-white' : 'text-gray-500 hover:bg-gray-50'}`}
          >
            New Template
          </button>
          <button
            type="button"
            onClick={() => setMode('override')}
            className={`flex-1 py-1.5 font-medium transition-colors ${mode === 'override' ? 'bg-brand-red text-white' : 'text-gray-500 hover:bg-gray-50'}`}
          >
            Override Existing
          </button>
        </div>

        {mode === 'new' ? (
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1.5">Template Name</label>
            <input
              autoFocus
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSave()}
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-red"
              placeholder="e.g. 3C Inspection, G5000 Upgrade…"
            />
          </div>
        ) : (
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1.5">Select Template to Override</label>
            {templatesLoading ? (
              <div className="flex items-center justify-center h-20 text-gray-400 text-xs">Loading…</div>
            ) : templates.length === 0 ? (
              <p className="text-xs text-gray-400 italic">No saved templates yet.</p>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-hidden divide-y divide-gray-100 max-h-40 overflow-y-auto">
                {templates.map(tpl => (
                  <button
                    key={tpl.id}
                    type="button"
                    onClick={() => selectTemplate(tpl)}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors ${selectedTpl?.id === tpl.id ? 'bg-brand-red/10 text-brand-red font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
                  >
                    <span className="truncate block">{tpl.name}</span>
                    {tpl.tasks?.length != null && (
                      <span className="text-xs text-gray-400 font-normal">{tpl.tasks.length} task{tpl.tasks.length !== 1 ? 's' : ''}</span>
                    )}
                  </button>
                ))}
              </div>
            )}
            {selectedTpl && (
              <div className="mt-3">
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1.5">Template Name</label>
                <input
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-red"
                  placeholder={selectedTpl.name}
                />
                <p className="text-xs text-gray-400 mt-1">Rename or leave as-is.</p>
              </div>
            )}
          </div>
        )}

        <div>
          <label className="block text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">
            Tasks to Save <span className="text-gray-300 normal-case font-normal">({projectSubtasks.length} tasks)</span>
          </label>
          {hasTasks ? (
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {deptGroups.map(dept => (
                <div key={dept}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: deptMap[dept]?.hex || '#6b7280' }} />
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{deptMap[dept]?.label || dept}</span>
                  </div>
                  {projectSubtasks.filter(s => s.department === dept).map(s => (
                    <div key={s.id} className="flex items-center justify-between pl-3.5 pr-1 py-0.5 text-xs">
                      <span className="text-gray-600 truncate flex-1">{s.task_name}</span>
                      <span className="text-gray-400 ml-3 flex-shrink-0 tabular-nums">{s.start_date} → {s.end_date}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic">No tasks on this project to save.</p>
          )}
        </div>

        {!project?.arrival_date && hasTasks && (
          <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            This project has no arrival date — tasks will be saved without day offsets.
          </p>
        )}

        {mode === 'override' && selectedTpl && (
          <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            This will replace all tasks in <span className="font-semibold">"{selectedTpl.name}"</span> with the current workflow.
          </p>
        )}

        {err && <p className="text-xs text-red-500">{err}</p>}

        <div className="flex gap-2 justify-end pt-1">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors">Cancel</button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            className="text-sm px-4 py-2 rounded-lg bg-brand-red text-white font-medium hover:bg-brand-red-dark disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {saved ? '✓ Saved!' : saving ? 'Saving…' : mode === 'override' ? 'Override Template' : 'Save Template'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Use Template Modal ────────────────────────────────────────────────────────

function UseTemplateModal({ defaultArrivalDate, onApply, onClose }) {
  const { profile } = useAuth()
  const orgDepts = profile?.orgs?.settings?.departments ?? []
  const deptMap = Object.fromEntries(orgDepts.map(d => [d.key, d]))

  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState(null)
  const [arrivalDate, setArrivalDate] = useState(defaultArrivalDate || '')
  const [confirmDeleteId, setConfirmDeleteId] = useState(null)

  useEffect(() => {
    fetchAllWorkflowTemplates()
      .then(({ data }) => { setTemplates(data || []); setLoading(false) })
  }, [])

  const deleteTemplate = async (id) => {
    await deleteWorkflowTemplate(id)
    setTemplates(ts => ts.filter(t => t.id !== id))
    if (selected?.id === id) setSelected(null)
    setConfirmDeleteId(null)
  }

  const filtered = search.trim()
    ? templates.filter(t =>
        t.name.toLowerCase().includes(search.toLowerCase()) ||
        (t.workscope || '').toLowerCase().includes(search.toLowerCase())
      )
    : templates

  const previewDepts = selected
    ? orgDepts.map(d => d.key).filter(d => (selected.tasks || []).some(t => t.department === d))
    : []

  const canApply = selected && arrivalDate

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl flex flex-col" style={{ width: 720, maxHeight: '84vh' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-gray-800">Use Template</h2>
            <p className="text-xs text-gray-400 mt-0.5">Select a saved workflow to populate this project's tasks</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name or workscope…"
              className="w-full text-sm border border-gray-200 rounded-lg pl-8 pr-3 py-2 focus:outline-none focus:border-brand-red"
            />
          </div>
        </div>

        {/* Body: list + preview */}
        <div className="flex flex-1 overflow-hidden min-h-0">
          {/* Template list */}
          <div className="w-52 border-r border-gray-100 overflow-y-auto flex-shrink-0">
            {loading ? (
              <div className="p-4 text-xs text-gray-400">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="p-4 text-xs text-gray-400 italic">
                {templates.length === 0
                  ? 'No templates saved yet. Use "Save as Template" on a project first.'
                  : 'No matches found.'}
              </div>
            ) : (
              filtered.map(t => {
                const taskCount = (t.tasks || []).length
                const isSelected = selected?.id === t.id
                const isConfirming = confirmDeleteId === t.id
                return (
                  <div
                    key={t.id}
                    className={`group relative border-b border-gray-50 border-l-2 transition-colors ${
                      isSelected ? 'bg-brand-red/10 border-l-brand-red' : 'hover:bg-gray-50 border-l-transparent'
                    }`}
                  >
                    <button
                      onClick={() => { setSelected(t); setConfirmDeleteId(null) }}
                      className="w-full text-left px-4 py-3 pr-8"
                    >
                      <div className={`text-sm font-medium truncate ${isSelected ? 'text-brand-red' : 'text-gray-700'}`}>{t.name}</div>
                      {t.workscope && t.workscope !== t.name && (
                        <div className="text-xs text-gray-400 truncate mt-0.5">{t.workscope}</div>
                      )}
                      <div className="text-xs text-gray-300 mt-0.5">{taskCount} task{taskCount !== 1 ? 's' : ''}</div>
                    </button>
                    {isConfirming ? (
                      <div className="absolute inset-0 flex items-center justify-center gap-1.5 bg-red-50 border-l-2 border-l-red-400 px-2">
                        <span className="text-xs text-red-600 font-medium">Delete?</span>
                        <button onClick={() => deleteTemplate(t.id)} className="text-xs px-2 py-0.5 rounded bg-red-600 text-white font-medium hover:bg-red-700">Yes</button>
                        <button onClick={() => setConfirmDeleteId(null)} className="text-xs px-2 py-0.5 rounded border border-gray-300 text-gray-500 hover:bg-gray-100">No</button>
                      </div>
                    ) : (
                      <button
                        onClick={e => { e.stopPropagation(); setConfirmDeleteId(t.id) }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-300 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Delete template"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                )
              })
            )}
          </div>

          {/* Preview */}
          <div className="flex-1 p-4 overflow-y-auto">
            {!selected ? (
              <div className="h-full flex items-center justify-center text-sm text-gray-400 italic">← Select a template to preview</div>
            ) : (
              <div className="space-y-3">
                {previewDepts.map(dept => (
                  <div key={dept} className="rounded-lg border border-gray-200 overflow-hidden">
                    <div
                      className="flex items-center gap-2 px-3 py-2 border-b border-gray-100"
                      style={{ backgroundColor: `${deptMap[dept]?.hex || '#6b7280'}12` }}
                    >
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: deptMap[dept]?.hex || '#6b7280' }} />
                      <span className="text-xs font-bold uppercase tracking-widest text-gray-600">{deptMap[dept]?.label || dept}</span>
                    </div>
                    <div className="divide-y divide-gray-50">
                      {(selected.tasks || []).filter(t => t.department === dept).map(t => (
                        <div key={t.id} className="flex items-center justify-between px-3 py-2">
                          <span className="text-sm text-gray-700">{t.task_name || <span className="italic text-gray-300">Unnamed</span>}</span>
                          <span className="text-xs text-gray-400 ml-4 flex-shrink-0 font-medium tabular-nums">
                            {t.start_offset_days != null
                              ? `Day ${t.start_offset_days + 1} → Day ${t.end_offset_days + 1}`
                              : '—'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {(selected.tasks || []).length === 0 && (
                  <p className="text-sm text-gray-400 italic text-center py-8">This template has no tasks.</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-4 px-6 py-4 border-t border-gray-100 bg-gray-50 flex-shrink-0 rounded-b-xl">
          <div className="flex items-center gap-2.5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-widest whitespace-nowrap">Arrival Date</label>
            <input
              type="date"
              value={arrivalDate}
              onChange={e => setArrivalDate(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-brand-red bg-white"
            />
          </div>
          {selected && !arrivalDate && (
            <span className="text-xs text-amber-600">Enter an arrival date to apply</span>
          )}
          <div className="flex-1" />
          <span className="text-xs text-gray-400">Replaces all existing tasks for this project</span>
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100 transition-colors">Cancel</button>
          <button
            onClick={() => canApply && onApply(selected, arrivalDate)}
            disabled={!canApply}
            className="text-sm px-5 py-2 rounded-lg bg-brand-red text-white font-medium hover:bg-brand-red-dark disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Apply Template
          </button>
        </div>
      </div>
    </div>
  )
}

// ── MAIN CALENDAR VIEW ────────────────────────────────────────────────────────

const SCHEDULE_STATUS_LABELS = { ahead: 'Ahead of Schedule', tracking: 'On Track', at_risk: 'At Risk', delayed: 'Delayed' }

export default function CalendarView({ projects, subtasks, aircraft, callouts = [], technicians = [], subtaskTechMap = {}, projectTechMap = {}, projectTechSet = {}, projectTechDates = [], onAddProjectTech, onRemoveProjectTech, onAddTechDate, onRemoveTechDate, subView, onClickProject, onClickSubtask, refetch, onHistory, onOpenNotes, notesKey = 0, isEditor, canEditSubtasks, onWorkOrderClick, userProfile, flightLegs = [], onAddToSchedule, snapshotForPrint, onSnapshotPrinted, loadProjectSubtasks }) {
  const { isTechnician, profile, hasFeature } = useAuth()
  const [selectedProjectId, setSelectedProjectId] = useState(() => {
    const stored = sessionStorage.getItem('selectedProjectId')
    if (stored) return stored
    // Default to first non-complete project; fall back to first project overall
    return (projects.find(p => p.status !== 'Complete') ?? projects[0])?.id ?? null
  })

  const selectProject = (id) => {
    sessionStorage.setItem('selectedProjectId', id)
    setSelectedProjectId(id)
  }

  const [showSidebar, setShowSidebar] = useState(false)
  const [dayPicker, setDayPicker] = useState(null)
  const [subtaskModal, setSubtaskModal] = useState(null)
  const [editProjectModal, setEditProjectModal] = useState(null)
  const [calloutModal, setCalloutModal] = useState(null)

  const selectedProject = useMemo(() =>
    projects.find(p => p.id === selectedProjectId) || null,
    [projects, selectedProjectId]
  )

  const projectSubtasks = useMemo(() =>
    subtasks.filter(s => s.project_id === selectedProjectId),
    [subtasks, selectedProjectId]
  )

  // Lazy-load subtasks for completed projects (excluded from the initial fetch)
  useEffect(() => {
    if (selectedProject?.status === 'Complete' && loadProjectSubtasks) {
      loadProjectSubtasks(selectedProjectId)
    }
  }, [selectedProjectId, selectedProject?.status, loadProjectSubtasks])

  const projectCallouts = useMemo(() =>
    callouts.filter(c => c.project_id === selectedProjectId),
    [callouts, selectedProjectId]
  )

  const [notesCount, setNotesCount] = useState(0)
  useEffect(() => {
    if (!selectedProjectId) { setNotesCount(0); return }
    let q = supabase
      .from('project_notes')
      .select('id', { count: 'exact', head: true })
      .eq('project_id', selectedProjectId)
    if (isTechnician) q = q.not('subtask_id', 'is', null)
    q.then(({ count }) => setNotesCount(count ?? 0))
  }, [selectedProjectId, notesKey, isTechnician])

  // Snapshot print: temporarily substitute historical data into the workflow view, then print
  useEffect(() => {
    if (!snapshotForPrint) return
    const snap = snapshotForPrint
    const registration = snap.state?.project?.aircraft?.registration || 'workflow'
    const [y, m, d] = (snap.snapshot_date || '').split('-')
    const datePart = y ? `${m}.${d}.${y.slice(2)}` : ''
    const originalTitle = document.title
    document.title = `${registration}_${datePart}`
    document.body.setAttribute('data-printing', 'workflows-export')
    setTimeout(() => {
      window.print()
      setTimeout(() => {
        document.title = originalTitle
        document.body.removeAttribute('data-printing')
        onSnapshotPrinted?.()
      }, 500)
    }, 100)
  }, [snapshotForPrint])

  // Due-list tab: Traxxall data keyed by reg, fetched on demand
  const [dueListCalData, setDueListCalData] = useState({})
  const reg = selectedProject?.aircraft?.registration || ''
  useEffect(() => {
    if (!reg) return
    if (dueListCalData[reg]?.items) return
    setDueListCalData(prev => ({ ...prev, [reg]: { loading: true, items: [], actuals: [] } }))
    prefetchTraxxallData(reg).then(result => {
      if (!result) return
      setDueListCalData(prev => ({
        ...prev,
        [reg]: { loading: false, items: result.items || [], actuals: result.actuals || [] },
      }))
    })
  }, [reg])

  const dueListAircraft = useMemo(() =>
    selectedProject?.aircraft ? [selectedProject.aircraft] : [],
    [selectedProject]
  )

  const dueListProjections = useMemo(
    () => buildAircraftProjections(dueListCalData),
    [dueListCalData]
  )

  const dueListTolExpiryProjections = useMemo(
    () => buildToleranceExpiryProjections(dueListCalData),
    [dueListCalData]
  )

  const dueListProjKeyMap = useMemo(() => {
    const regMap = dueListProjections[reg]
    if (!regMap) return {}
    const result = {}
    for (const [item, proj] of regMap) {
      if (!proj?.projDate) continue
      const k = [item['taskNo'] || '', item['cw Date'] || '', item['ma'] || '', item['position'] || ''].join('\x00')
      result[k] = proj
    }
    return result
  }, [dueListProjections, reg])

  // Legacy per-project QA seeder: only runs for orgs pre-dating auto-milestones settings.
  const qaSyncKey = `${selectedProjectId}|${selectedProject?.arrival_date}|${selectedProject?.departure_date}`
  const lastQASyncRef = useRef(null)
  useEffect(() => {
    if (profile?.orgs?.settings?.autoMilestones !== undefined) return
    if (!isEditor || !selectedProject?.arrival_date || !selectedProject?.departure_date) return
    if (lastQASyncRef.current === qaSyncKey) return
    lastQASyncRef.current = qaSyncKey

    const existing = subtasks.filter(s => s.project_id === selectedProjectId && s.department === 'QA' && QA_MILESTONE_NAMES.has(s.task_name))
    if (existing.length > 0) { markQASeeded(selectedProjectId); return }

    // Project was previously seeded and now has none — user deleted them intentionally
    if (getQASeededSet().has(selectedProjectId)) return

    const expected = generateQASubtasks(selectedProjectId, selectedProject.arrival_date, selectedProject.departure_date)
    if (expected.length === 0) return
    createSubtasks(expected)
      .then(() => { markQASeeded(selectedProjectId); refetch() })
      .catch(err => console.error('[QA subtasks seed]', err))
  }, [qaSyncKey, isEditor])  // eslint-disable-line react-hooks/exhaustive-deps

const openDayPicker = useCallback((date, subtasks, projectId, anchorRect) => {
    setDayPicker({ date, subtasks, projectId, anchorRect })
  }, [])

  const handleReorder = useCallback(async (subtask, direction, chunkSubtasks) => {
    // Scope to chunk if provided; otherwise fall back to all project subtasks
    const pool = (chunkSubtasks?.length ? chunkSubtasks : projectSubtasks)
      .slice()
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id.localeCompare(b.id))
    const idx = pool.findIndex(s => s.id === subtask.id)
    if (idx === -1) return
    const neighborIdx = direction === 'up' ? idx - 1 : idx + 1
    if (neighborIdx < 0 || neighborIdx >= pool.length) return
    // Swap positions in the array then write clean sequential values so that
    // equal or null sort_orders never cause a no-op swap
    ;[pool[idx], pool[neighborIdx]] = [pool[neighborIdx], pool[idx]]
    await Promise.all(pool.map((s, i) => updateSubtask(s.id, { sort_order: i * 10 })))
    refetch()
  }, [projectSubtasks, refetch])

  const handleUpdateAsOf = useCallback(async (isoString) => {
    if (!selectedProjectId) return
    await updateProject(selectedProjectId, { workflow_as_of: isoString })
    refetch()
  }, [selectedProjectId, refetch])

  const handleUpdateDates = useCallback(async (field, value) => {
    if (!selectedProjectId) return
    const oldValue = selectedProject?.[field]
    await updateProject(selectedProjectId, { [field]: value })

    // Log the project date change
    await logChange({
      projectId: selectedProjectId,
      entityType: 'project',
      entityId: selectedProjectId,
      entityLabel: selectedProject?.aircraft?.registration,
      action: 'updated',
      oldData: { [field]: oldValue ?? null },
      newData: { [field]: value },
      userProfile,
      aircraft: [],
    })

    if (field === 'arrival_date') {
      const oldArrival = oldValue
      const newArrival = value
      if (oldArrival && newArrival && oldArrival !== newArrival) {
        const delta = businessDayDelta(nextBusinessDay(oldArrival), nextBusinessDay(newArrival))
        if (delta !== 0) {
          const projectSubtasks = subtasks.filter(s => s.project_id === selectedProjectId && s.start_date)
          const projectCallouts = callouts.filter(c => c.project_id === selectedProjectId && c.date)

          // Compute new dates before firing updates so we can log them
          const subtaskShifts = projectSubtasks.map(s => ({
            s,
            newStart: addBusinessDays(s.start_date, delta),
            newEnd:   s.end_date ? addBusinessDays(s.end_date, delta) : s.end_date,
          }))

          await Promise.all([
            ...subtaskShifts.map(({ s, newStart, newEnd }) =>
              updateSubtask(s.id, { start_date: newStart, end_date: newEnd })
            ),
            ...projectCallouts.map(c => updateCallout(c.id, {
              date:     addBusinessDays(c.date, delta),
              end_date: c.end_date ? addBusinessDays(c.end_date, delta) : c.end_date,
            })),
          ])

          // Log each subtask shift
          await Promise.all(
            subtaskShifts.map(({ s, newStart, newEnd }) => logChange({
              projectId: selectedProjectId,
              entityType: 'subtask',
              entityId: s.id,
              entityLabel: s.task_name,
              action: 'updated',
              oldData: { start_date: s.start_date, end_date: s.end_date },
              newData: { start_date: newStart, end_date: newEnd },
              userProfile,
            }))
          )
        }
      }
    }

    if (field === 'departure_date') {
      const oldDeparture = oldValue
      const newDeparture = value
      if (oldDeparture && newDeparture && oldDeparture !== newDeparture) {
        const delta = businessDayDelta(nextBusinessDay(oldDeparture), nextBusinessDay(newDeparture))
        if (delta !== 0) {
          const rtsSubtasks = subtasks.filter(s =>
            s.project_id === selectedProjectId &&
            s.start_date &&
            (s.task_name === 'RTS' || s.task_name === 'INCOMING / RTS')
          )
          if (rtsSubtasks.length > 0) {
            const rtsShifts = rtsSubtasks.map(s => ({
              s,
              newStart: addBusinessDays(s.start_date, delta),
              newEnd:   s.end_date ? addBusinessDays(s.end_date, delta) : s.end_date,
            }))
            await Promise.all(
              rtsShifts.map(({ s, newStart, newEnd }) =>
                updateSubtask(s.id, { start_date: newStart, end_date: newEnd })
              )
            )
            await Promise.all(
              rtsShifts.map(({ s, newStart, newEnd }) => logChange({
                projectId: selectedProjectId,
                entityType: 'subtask',
                entityId: s.id,
                entityLabel: s.task_name,
                action: 'updated',
                oldData: { start_date: s.start_date, end_date: s.end_date },
                newData: { start_date: newStart, end_date: newEnd },
                userProfile,
              }))
            )
          }
        }
      }
    }

    refetch()
  }, [selectedProjectId, selectedProject, subtasks, callouts, userProfile, refetch])

  const closeDayPicker = () => setDayPicker(null)

  const [showSaveModal, setShowSaveModal] = useState(false)
  const [showUseModal, setShowUseModal] = useState(false)
  const [showUpdateModal, setShowUpdateModal] = useState(false)
  const [existingTemplate, setExistingTemplate] = useState(null)

  useEffect(() => {
    if (!selectedProjectId) { setExistingTemplate(null); return }
    fetchLatestWorkflowTemplate(selectedProjectId)
      .then(({ data }) => setExistingTemplate(data?.[0] || null))
  }, [selectedProjectId])

  async function saveTemplate(name) {
    const arrivalDate = selectedProject?.arrival_date
    const tasks = projectSubtasks.map(s => ({
      id: crypto.randomUUID(),
      task_name: s.task_name,
      department: s.department,
      start_offset_days: arrivalDate ? businessDaysBetween(arrivalDate, s.start_date) : null,
      end_offset_days: arrivalDate ? businessDaysBetween(arrivalDate, s.end_date) : null,
    }))
    const { data, error } = await createWorkflowTemplate({
      name,
      workscope: selectedProject?.workscope || '',
      tasks,
      source_project_id: selectedProjectId,
    })
    if (error) throw error
    setExistingTemplate(data)
  }

  async function updateTemplate(templateId, name) {
    const arrivalDate = selectedProject?.arrival_date
    const tasks = projectSubtasks.map(s => ({
      id: crypto.randomUUID(),
      task_name: s.task_name,
      department: s.department,
      start_offset_days: arrivalDate ? businessDaysBetween(arrivalDate, s.start_date) : null,
      end_offset_days: arrivalDate ? businessDaysBetween(arrivalDate, s.end_date) : null,
    }))
    const { error } = await updateWorkflowTemplate(templateId, { name, tasks })
    if (error) throw error
    setExistingTemplate(t => ({ ...t, name, tasks }))
  }

  async function applyTemplate(template, arrivalDate) {
    if (!selectedProjectId) return
    await deleteProjectSubtasks(selectedProjectId)
    const newSubtasks = (template.tasks || []).map(t => ({
      project_id: selectedProjectId,
      task_name: t.task_name,
      department: t.department,
      start_date: t.start_offset_days != null ? addBusinessDays(arrivalDate, t.start_offset_days) : arrivalDate,
      end_date: t.end_offset_days != null ? addBusinessDays(arrivalDate, t.end_offset_days) : arrivalDate,
      status: 'Scheduled',
    }))
    if (newSubtasks.length > 0) {
      const { error } = await createSubtasks(newSubtasks)
      if (error) { console.error('[applyTemplate]', error); return }
    }
    refetch()
    setShowUseModal(false)
  }

  return (
    <div>
      {/* Project selector bar */}
      <div className="no-print flex items-center gap-2 mb-4 flex-wrap">
        <button onClick={() => setShowSidebar(true)}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700">
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
          </svg>
          {selectedProject
            ? `${selectedProject.aircraft?.registration || '—'}${selectedProject.customer_name ? ' · ' + selectedProject.customer_name : ''}`
            : 'Select a project'}
          <svg className="w-4 h-4 text-gray-400 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {selectedProject && (
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
            selectedProject.status === 'Complete' ? 'bg-green-100 text-green-700' :
            selectedProject.status === 'In Work'  ? 'bg-blue-100 text-blue-700' :
            'bg-gray-100 text-gray-600'
          }`}>
            {selectedProject.status}
          </span>
        )}

        {selectedProject && subView !== 'customer-update' && subView !== 'due-list' && (
          <div className="flex items-center gap-2 flex-wrap">
            {isEditor && (
              <>
                {existingTemplate ? (
                  <button
                    onClick={() => setShowUpdateModal(true)}
                    className="flex items-center gap-1.5 text-xs text-green-700 font-medium border border-green-300 rounded-lg px-3 py-1.5 bg-green-50 hover:bg-green-100 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                    </svg>
                    Saved as Template
                  </button>
                ) : (
                  <button
                    onClick={() => setShowSaveModal(true)}
                    className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 transition-colors bg-white"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                    </svg>
                    Save as Template
                  </button>
                )}
                <button
                  onClick={() => setShowUseModal(true)}
                  className="flex items-center gap-1.5 text-xs text-brand-red hover:text-brand-red-dark font-medium border border-brand-red/20 rounded-lg px-3 py-1.5 bg-brand-red/10 hover:bg-brand-red/20 transition-colors"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Use Template
                </button>
              </>
            )}
            {subView === 'department' ? (
              <ExportButton
                elementId="workflows-dept-export"
                filename={`${selectedProject.aircraft?.registration || 'project'}-dept-workflow-${new Date().toISOString().slice(0, 10)}`}
              />
            ) : (
              <ExportButton
                elementId="workflows-export"
                filename={`${selectedProject.aircraft?.registration || 'project'}-workflow-${new Date().toISOString().slice(0, 10)}`}
              />
            )}
          </div>
        )}
      </div>

      {/* No project selected */}
      {!selectedProject && (
        <div className="flex flex-col items-center justify-center py-24 text-gray-400 gap-3">
          <svg className="w-10 h-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-sm">Select a project to view its workflow</p>
          <button onClick={() => setShowSidebar(true)} className="text-sm text-brand-red hover:text-brand-red-dark font-medium">
            Browse projects →
          </button>
        </div>
      )}

      {/* Sticky project header + content — single container so sticky has room to operate */}
      {selectedProject && (
        <div>
          <div className={`md:sticky top-0 z-30 ${subView === 'customer-update' ? 'no-print' : ''}`}>
            <ProjectHeader
              project={snapshotForPrint ? snapshotForPrint.state.project : selectedProject}
              onEdit={isEditor ? () => setEditProjectModal(selectedProject) : undefined}
              onNotes={onOpenNotes ? () => { const reg = selectedProject.aircraft?.registration || selectedProject.secondary_registration || selectedProject.work_order || '—'; onOpenNotes(selectedProject.id, reg, { required: false }) } : undefined}
              notesCount={notesCount}
              onDelete={isEditor ? async () => {
                const reg = selectedProject.aircraft?.registration || selectedProject.work_order || 'this project'
                if (!confirm(`Delete ${reg} and all its subtasks? This cannot be undone.`)) return
                await logChange({
                  projectId: selectedProjectId, entityType: 'project', entityId: selectedProjectId,
                  entityLabel: reg, action: 'deleted', userProfile,
                })
                await deleteProject(selectedProjectId)
                refetch()
              } : undefined}
              onHistory={onHistory}
              onUpdateAsOf={isEditor ? handleUpdateAsOf : undefined}
              onUpdateDates={isEditor ? handleUpdateDates : undefined}
              projectTechs={projectTechMap[selectedProjectId] || []}
              projectTechSet={projectTechSet[selectedProjectId] || null}
              allTechnicians={technicians}
              subtasks={subtasks}
              subtaskTechMap={subtaskTechMap}
              projectTechDates={projectTechDates.filter(d => d.project_id === selectedProjectId)}
              onAddProjectTech={isEditor ? onAddProjectTech : undefined}
              onRemoveProjectTech={isEditor ? onRemoveProjectTech : undefined}
              onAddTechDate={onAddTechDate}
              onRemoveTechDate={onRemoveTechDate}
              onWorkOrderClick={onWorkOrderClick}
            />
          </div>
          <div className="mt-4">
          {subView === 'department' ? (
            <BiweeklyDeptView
              project={selectedProject}
              subtasks={subtasks}
              onClickSubtask={onClickSubtask}
              onDayClick={openDayPicker}
              subtaskTechMap={subtaskTechMap}
            />
          ) : (
            <>
              {/* CustomerUpdatePanel — always mounted to preserve state; hidden when inactive */}
              <div className={subView !== 'customer-update' ? 'hidden' : ''}>
                <CustomerUpdatePanel
                  key={selectedProjectId}
                  projectId={selectedProjectId}
                  project={selectedProject}
                  subtasks={projectSubtasks}
                  isEditor={canEditSubtasks}
                />
              </div>

              {/* Due List — calendar + full due list */}
              <div className={subView !== 'due-list' ? 'hidden' : ''}>
                {reg && (
                  <div className="mb-4">
                    <FleetDueCalendar
                      enrolledAircraft={dueListAircraft}
                      allData={dueListCalData}
                      projections={dueListProjections}
                      tolExpiryProjections={dueListTolExpiryProjections}
                      onTaskClick={onWorkOrderClick}
                      flightLegs={flightLegs}
                    />
                  </div>
                )}
                <TraxxallDueList
                  key={selectedProjectId}
                  registration={reg}
                  altRegistration={selectedProject?.secondary_registration || ''}
                  workOrderNumbers={[selectedProject?.work_order, ...(selectedProject?.work_orders_extra || [])].filter(Boolean)}
                  projectStatus={selectedProject?.status || ''}
                  onTaskClick={onWorkOrderClick}
                  projections={dueListProjKeyMap}
                  onAddToSchedule={onAddToSchedule}
                  hideCountBadges
                />
              </div>

              {/* Workflow + Charts — hidden when customer update or due list tab is active */}
              <div className={subView === 'customer-update' || subView === 'due-list' ? 'hidden' : ''} id="workflows-export">
                {/* Workflow — hidden on screen when Charts tab active, always prints */}
                <div className={subView === 'charts' ? 'no-screen' : ''}>
                  <ProjectView
                    project={snapshotForPrint ? snapshotForPrint.state.project : selectedProject}
                    subtasks={snapshotForPrint ? snapshotForPrint.state.subtasks : projectSubtasks}
                    snapshotDate={snapshotForPrint?.snapshot_date}
                    callouts={snapshotForPrint ? (snapshotForPrint.state.callouts || []) : projectCallouts}
                    onClickSubtask={onClickSubtask}
                    onDayClick={openDayPicker}
                    subtaskTechMap={subtaskTechMap}
                    flightLegs={flightLegs}
                    onReorder={isEditor ? handleReorder : undefined}
                    onAddCallout={canEditSubtasks ? (date) => setCalloutModal({ projectId: selectedProjectId, callout: null, defaultDate: date }) : undefined}
                    onEditCallout={canEditSubtasks ? (c) => setCalloutModal({ projectId: selectedProjectId, callout: c, defaultDate: c.date }) : undefined}
                    onDeleteCallout={canEditSubtasks ? async (c) => {
                      if (!confirm(`Delete "${c.text}"?`)) return
                      await logChange({
                        projectId: selectedProjectId, entityType: 'callout', entityId: c.id,
                        entityLabel: c.text, action: 'deleted', userProfile,
                      })
                      await deleteCallout(c.id)
                      refetch()
                    } : undefined}
                  />
                </div>
                {/* Charts — only rendered when org has tsheets feature; hidden off-screen when not active so recharts has real width */}
                {hasFeature('tsheets') && (
                  <div className={`charts-section${subView !== 'charts' ? ' charts-for-print' : ''}`}>
                    <ProjectChartsPanel
                      key={selectedProjectId}
                      projectId={selectedProjectId}
                      project={selectedProject}
                      snapshotState={snapshotForPrint?.state}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* As Of footer */}
        {selectedProject.workflow_as_of && (
          <div className="flex items-center justify-center gap-2 py-2 border-t border-gray-100 mt-2">
            <span className="text-xs text-gray-400">
              As of {new Date(selectedProject.workflow_as_of).toLocaleString('en-US', {
                month: 'numeric', day: 'numeric', year: '2-digit',
                hour: 'numeric', minute: '2-digit',
              })}
            </span>
            {isEditor && (
              <button
                onClick={() => handleUpdateAsOf(new Date().toISOString())}
                className="no-print text-xs text-brand-red hover:text-brand-red-dark font-medium"
                title="Set As Of to now"
              >
                Update
              </button>
            )}
          </div>
        )}
        </div>
      )}

      {/* Day picker */}
      {dayPicker && (
        <DayPickerPopover
          date={dayPicker.date}
          subtasks={dayPicker.subtasks}
          anchorRect={dayPicker.anchorRect}
          groupBy="department"
          onClose={closeDayPicker}
          onEditSubtask={canEditSubtasks ? (s) => { closeDayPicker(); setSubtaskModal({ projectId: s.project_id, subtask: s }) } : undefined}
          onDeleteSubtask={canEditSubtasks ? async (s) => {
            if (!confirm(`Delete "${s.task_name}"?`)) return
            await deleteSubtask(s.id)
            closeDayPicker()
            refetch()
          } : undefined}
          onAddSubtask={canEditSubtasks ? () => {
            closeDayPicker()
            setSubtaskModal({ projectId: dayPicker.projectId, subtask: null, defaultStartDate: dayPicker.date, defaultEndDate: dayPicker.date })
          } : undefined}
        />
      )}

      {/* Subtask modal */}
      {subtaskModal && (
        <SubtaskModal
          projectId={subtaskModal.projectId}
          subtask={subtaskModal.subtask}
          defaultStartDate={subtaskModal.defaultStartDate}
          defaultEndDate={subtaskModal.defaultEndDate}
          technicians={technicians}
          userProfile={userProfile}
          onClose={() => setSubtaskModal(null)}
          onSaved={() => { setSubtaskModal(null); refetch() }}
        />
      )}

      {/* Project edit modal */}
      {editProjectModal && (
        <ProjectModal
          project={editProjectModal}
          subtasks={subtasks}
          callouts={callouts}
          aircraft={aircraft}
          userProfile={userProfile}
          technicians={technicians}
          assignedTechIds={projectTechSet[editProjectModal?.id] || new Set()}
          onAddProjectTech={isEditor ? onAddProjectTech : undefined}
          onRemoveProjectTech={isEditor ? onRemoveProjectTech : undefined}
          onClose={() => setEditProjectModal(null)}
          onSaved={() => { setEditProjectModal(null); refetch() }}
          onScheduleStatusChanged={(projectId, oldStatus, newStatus) => {
            if (!onOpenNotes) return
            const p = projects.find(proj => proj.id === projectId)
            const reg = p?.aircraft?.registration || p?.secondary_registration || '—'
            const oldLabel = SCHEDULE_STATUS_LABELS[oldStatus] || oldStatus
            const newLabel = SCHEDULE_STATUS_LABELS[newStatus] || newStatus
            onOpenNotes(projectId, reg, { required: true, prompt: `Risk level changed from "${oldLabel}" to "${newLabel}" — please add a note explaining why.` })
          }}
          onSubtasksUpdated={refetch}
        />
      )}

      {/* Callout modal */}
      {calloutModal && (
        <CalloutModal
          projectId={calloutModal.projectId}
          callout={calloutModal.callout}
          defaultDate={calloutModal.defaultDate}
          userProfile={userProfile}
          onClose={() => setCalloutModal(null)}
          onSaved={() => { setCalloutModal(null); refetch() }}
        />
      )}

      {/* Project sidebar */}
      {showSidebar && (
        <ProjectSidebar
          projects={projects}
          selectedId={selectedProjectId}
          onSelect={selectProject}
          onClose={() => setShowSidebar(false)}
          onWorkOrderClick={onWorkOrderClick}
        />
      )}

      {showSaveModal && selectedProject && (
        <SaveTemplateModal
          project={selectedProject}
          projectSubtasks={projectSubtasks}
          onSave={saveTemplate}
          onOverride={updateTemplate}
          onClose={() => setShowSaveModal(false)}
        />
      )}
      {showUseModal && (
        <UseTemplateModal
          defaultArrivalDate={selectedProject?.arrival_date || ''}
          onApply={applyTemplate}
          onClose={() => setShowUseModal(false)}
        />
      )}
      {showUpdateModal && existingTemplate && (
        <UpdateTemplateModal
          template={existingTemplate}
          projectSubtasks={projectSubtasks}
          onUpdate={updateTemplate}
          onClose={() => setShowUpdateModal(false)}
        />
      )}
    </div>
  )
}